import { m365AdminTr } from './m365-admin.tr.js';
import { m365ChatTr, m365StepToolsTr } from './m365-chat.tr.js';
import { m365FilesTr } from './m365-files.tr.js';
import { m365ErrorsTr, m365UserTr } from './m365-user.tr.js';

/** Turkish UI texts (source of keys). Plain language, suggested action first (D-39). */
export const tr = {
  common: {
    yes: 'evet',
    no: 'hayır',
    optional: '(isteğe bağlı)',
    appName: 'Oppy',
    loading: 'Yükleniyor…',
    save: 'Kaydet',
    saved: 'Kaydedildi.',
    cancel: 'Vazgeç',
    create: 'Oluştur',
    delete: 'Sil',
    rename: 'Yeniden adlandır',
    back: 'Geri',
    next: 'Devam',
    close: 'Kapat',
    retry: 'Yeniden dene',
    technicalDetails: 'Teknik ayrıntılar',
    confirmDelete: 'Silmek istediğinize emin misiniz?',
    add: 'Ekle',
    you: 'siz',
    requestId: 'İstek kimliği',
    errorCode: 'Hata kodu',
  },
  nav: {
    dashboard: 'Ana sayfa',
    team: 'Ekip',
    settings: 'Ayarlar',
    memory: 'Hafıza',
    skills: "Skill'ler",
    connectors: 'Bağlantılar',
    documents: 'Belgeler',
    logout: 'Çıkış yap',
    language: 'Dil',
    organization: 'Firma',
    admin: 'Yönetim',
    platform: 'Platform',
    mainNav: 'Ana menü',
  },
  shell: {
    newConversation: 'Yeni sohbet',
    home: 'Ana sayfa',
    skills: "Skill'ler",
    connections: 'Bağlantılar',
    documents: 'Belgeler',
    places: 'Sayfalar',
    workspaces: 'Çalışma alanları',
    newWorkspace: 'Yeni çalışma alanı',
    allWorkspaces: 'Tümünü gör',
    noWorkspaces: 'Henüz çalışma alanı yok.',
    codeBadge: 'Kod',
    management: 'Yönetim',
    access: 'Erişim ve yetkiler',
    models: 'Modeller',
    integrations: 'Entegrasyonlar',
    usage: 'Kullanım ve limitler',
    platform: 'Platform',
    history: 'Sohbet geçmişi',
    settings: 'Ayarlar',
    settingsMenu: 'Ayarlar menüsü',
    collapse: 'Menüyü daralt',
    expand: 'Menüyü genişlet',
    openMenu: 'Menüyü aç',
    closeMenu: 'Menüyü kapat',
    skip: 'İçeriğe geç',
    experience: 'Ürün seç',
    experienceHint: { chat: 'Sohbet', code: 'Kod' },
    personalSpace: '{{name}} alanı',
    switchTenant: 'Firma değiştir',
  },
  home: {
    domains: 'Ekip alanlarınız',
    title: 'Size nasıl yardımcı olabilirim?',
    titleCode: 'Bugün ne yapalım?',
    placeholder: 'Bir şey sorun veya belge ekleyin',
    placeholderCode: 'Bir görev verin; plan, dosya ve komutlar burada',
    inputLabel: 'Mesajınız',
    add: 'Dosya, skill veya bağlantı ekle',
    attachFile: 'Dosya ekle',
    useSkill: 'Skill kullan',
    addConnection: 'Bağlantı ekle',
    files: 'Eklenecek dosyalar',
    tooLarge: 'Bir dosya {{mb}} MB sınırını aşıyor.',
    defaultModel: 'Varsayılan model',
    in: 'Çalışma alanı:',
    inWorkspace: '{{name}} çalışma alanında başlar',
    // D-219: a new chat starts in the person's own workspace unless they pick another
    inPersonal: 'Kişisel alanınızda başlar',
    personalName: 'Kişisel',
    change: 'değiştir',
    noWorkspace: 'Sohbet başlatmak için önce bir sohbet alanı oluşturun.',
    // D-152: in CompanyCode the first message opens the user's own code workspace
    personalCode: 'İlk mesajınızla size ait bir kod alanı açılır.',
    noModel: 'Kullanılabilir bir model yok; yöneticinizden bir model tanımlamasını isteyin.',
    waiting_one: 'Sizi bekleyen {{count}} iş var',
    waiting_other: 'Sizi bekleyen {{count}} iş var',
    suggestionsLabel: 'Başlangıç önerileri',
    discover: "Skill'leri ve bağlantıları keşfedin",
    suggestions: {
      chat: {
        contract: { label: 'Bu <b>sözleşmedeki</b> önemli maddeleri sayfa numaralarıyla özetle', text: 'Bu sözleşmedeki önemli maddeleri sayfa numaralarıyla özetle' },
        excel: { label: "Ekim satış <b>Excel'inden</b> grafikli bir rapor hazırla", text: "Ekim satış Excel'inden grafikli bir rapor hazırla" },
        web: { label: "Rakiplerimizin güncel fiyatlarını <b>web'de</b> araştır", text: "Rakiplerimizin güncel fiyatlarını web'de araştır" },
      },
      code: {
        tests: { label: 'Bu projedeki <b>testleri</b> çalıştır ve hataları düzelt', text: 'Bu projedeki testleri çalıştır ve hataları düzelt' },
        game: { label: 'Basit bir yılan oyunu yap ve <b>önizlemede</b> aç', text: 'Basit bir yılan oyunu yap ve önizlemede aç' },
        // v0.15a.3: where previews cannot open
        gameFiles: { label: 'Basit bir yılan oyunu yap, <b>tek bir HTML</b> dosyası olsun', text: 'Basit bir yılan oyunu yap; tek bir HTML dosyası olsun, indirip tarayıcıda açayım' },
        readme: { label: '<b>README</b> dosyasını projeye göre güncelle', text: 'README dosyasını projeye göre güncelle' },
      },
    },
  },
  workspaces: {
    filter: 'Çalışma alanı türü',
    filters: { all: 'Tümü', chat: 'Sohbet', code: 'Kod' },
    counts: {
      conversations_one: '{{count}} sohbet',
      conversations_other: '{{count}} sohbet',
      documents_one: '{{count}} belge',
      documents_other: '{{count}} belge',
      projects_one: '{{count}} proje',
      projects_other: '{{count}} proje',
      skills_one: '{{count}} skill',
      skills_other: '{{count}} skill',
    },
    members_one: '{{count}} üye',
    members_other: '{{count}} üye',
    newHint: 'Ekip, proje ya da konu için',
    all: 'Tümü',
    noneOfKind: 'Bu türde çalışma alanı yok.',
    row: {
      plan: 'Plan adım {{done}}/{{total}}',
      files_one: '{{count}} dosya',
      files_other: '{{count}} dosya',
      documents_one: '{{count}} belge',
      documents_other: '{{count}} belge',
    },
    now: {
      label: 'Şu an',
      waiting: 'Sizi bekleyenler',
      running: 'Çalışanlar',
      nothingWaits: 'Sizi bekleyen bir şey yok.',
      nothingRuns: 'Şu an çalışan bir şey yok.',
      questions_one: 'Bir soru yanıt bekliyor',
      questions_other: '{{count}} soru yanıt bekliyor',
      approval: 'Onay bekliyor: {{title}}',
      until: '{{time}} saatine kadar',
      progress: '{{total}} adımın {{done}} tanesi bitti',
    },
    intro: 'Sohbetler, belgeler ve kod projeleri burada gruplanır.',
    new: {
      title: 'Çalışma alanı oluştur',
      kind: 'Türü',
      hint: { chat: 'Sohbet, belgeler, web ve dosyalar', code: 'Kod, terminal, git ve önizleme' },
    },
  },
  settingsDialog: {
    title: 'Ayarlar',
    close: 'Ayarları kapat',
    sectionsLabel: 'Ayar bölümleri',
    openManagement: 'Yönetimi aç',
    sections: {
      general: 'Genel',
      account: 'Hesap',
      notifications: 'Bildirimler',
      memory: 'Hafıza',
      subscription: 'Abonelik',
      organization: 'Firma',
      limits: 'Limitler ve kotalar',
      policies: 'Onay politikaları',
      email: 'E-posta',
      audit: 'Denetim kaydı',
      identity: 'Kimlik doğrulama',
    },
    general: {
      theme: 'Görünüm',
      themes: { light: 'Açık', dark: 'Koyu', system: 'Sistem' },
      language: 'Dil',
      timeZoneBrowser: 'Tarayıcınızdan alındı',
      timeZoneChosen: 'Sizin seçiminiz',
      defaultModel: 'Varsayılan model',
      defaultCodeModel: 'Kod için varsayılan model',
      orgChoice: 'Firmanın seçimi',
      orgModel: 'Firmanın seçimi ({{model}})',
      memoryHint: 'Oppy yalnız siz istediğinizde bir şeyi hatırlar. Kaydedilenleri Hafıza bölümünde görüp silebilirsiniz.',
      data: 'Verilerim',
      dataHint: 'Bu firmadaki sohbet, dosya ve belgeleriniz',
      version: 'Oppy sürümü',
      versionHint: 'Bu kurulumun sunucu sürümü · {{vendor}}',
      download: 'İndir',
    },
    account: { providers: 'Kendi sağlayıcılarım' },
    notifications: {
      browser: 'Tarayıcı bildirimleri',
      allow: 'Bildirimlere izin ver',
      state: {
        default: 'Onay bekleyen bir iş olduğunda bu sekme kapalıyken de haber verilir.',
        granted: 'Açık: onay bekleyen işler bu sekme kapalıyken de bildirilir.',
        denied: 'Tarayıcı bildirimleri engelli. Tarayıcının site ayarlarından açabilirsiniz.',
        unsupported: 'Bu tarayıcı bildirim göstermiyor.',
      },
      badge: { granted: 'Açık', denied: 'Engelli', unsupported: 'Desteklenmiyor', default: 'Kapalı' },
      center: 'Bildirim merkezi',
      centerHint: 'Üst çubuktaki zil: okunmamışlar sayıyla gösterilir.',
      what: 'Neler bildirilir',
      kinds: {
        approval: 'Onayınızı bekleyen işlemler',
        question: 'Asistanın size sorduğu sorular',
        task: 'Biten arka plan işleri',
        failed: 'Yarım kalan ya da başarısız yanıtlar',
      },
    },
    email: {
      sent: 'Gönderilen e-postalar',
      kinds: { verify: 'Hesap doğrulama', reset: 'Parola sıfırlama', invite: 'Firmaya davet' },
      language: 'E-posta dili',
      languageHint: 'Her kişiye kendi arayüz dilinde gönderilir.',
      budget: 'Bütçe uyarıları',
      budgetHint: 'Bütçenin %80 ve %100 eşiğinde yöneticilere tek e-posta.',
      soon: 'Yakında',
    },
  },
  lang: { tr: 'Türkçe', en: 'English' },
  roles: {
    tenant: { owner: 'Süper yönetici', admin: 'Yönetici', member: 'Üye' },
    domainAdmin: 'Ekip yöneticisi',
    teams_one: '{{count}} ekip',
    teams_other: '{{count}} ekip',
    workspace: { owner: 'Oluşturan', editor: 'Düzenleyen', viewer: 'Görüntüleyen' },
  },
  kinds: { chat: 'Sohbet alanı', code: 'Kod çalışma alanı', personal: 'Kişisel' },
  auth: {
    // Phase 13d: the sign-in pages are in English in both languages (GirisKurumsal.dc.html, GirisKurumsalHata.dc.html)
    directory: {
      title: 'Welcome to Oppy',
      subtitle: 'New here or coming back? Choose how you want to continue.',
      email: 'Email',
      password: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      remember: 'Remember for 30 days',
      forgot: 'Forgot password',
      forgotNote: 'Your password is reset by your company’s IT team. After several wrong attempts you’ll be asked to wait a moment.',
      submit: 'Sign in',
      footer: 'Your password is checked by your company’s identity system. Oppy never stores it.',
      errors: {
        invalid: { title: 'Incorrect email or password.', action: 'Please try again.' },
        unavailable: { title: 'The sign-in service can’t be reached right now.', action: 'Please try again in a few minutes.' },
        locked: { title: 'Too many attempts.', action: 'Please wait a moment and try again.' },
        deactivated: { title: 'Your access to Oppy is turned off.', action: 'Please ask your administrator.' },
        relink: { title: 'Your company account has changed.', action: 'An administrator needs to confirm it before you can sign in. Please ask your administrator.' },
        check: { title: 'Please check what you entered.', action: 'Then try again.' },
        failed: { title: 'Sign-in didn’t work.', action: 'Please reload the page and try again.' },
      },
      // Phase 13d-2 (D-114): how long the wait after failed attempts still lasts
      wait_minutes_one: 'Try again in {{count}} minute.',
      wait_minutes_other: 'Try again in {{count}} minutes.',
      wait_seconds_one: 'Try again in {{count}} second.',
      wait_seconds_other: 'Try again in {{count}} seconds.',
      waitOver: 'You can try again now.',
      countdown: 'Sign in ({{time}})',
    },
    breakGlass: {
      title: 'Emergency sign-in',
      subtitle: 'For the break-glass admin, when the company sign-in can’t be used.',
      code: 'Authentication code',
      codeHint: 'The 6 digits your authenticator app shows.',
      codeInvalid: 'Enter the 6-digit code from your authenticator app.',
      footer: 'Every emergency sign-in is recorded in the audit log.',
      errors: {
        invalid: { title: 'Incorrect email, password or code.', action: 'Please try again.' },
        unavailable: { title: 'Oppy can’t be reached right now.', action: 'Please try again in a few minutes.' },
        locked: { title: 'Too many attempts.', action: 'Please wait a moment and try again.' },
        deactivated: { title: 'This account is turned off.', action: 'Please ask another owner.' },
        relink: { title: 'This account cannot sign in here.', action: 'Please ask another owner.' },
        check: { title: 'Please check what you entered.', action: 'Then try again.' },
        failed: { title: 'Sign-in didn’t work.', action: 'Please reload the page and try again.' },
      },
    },
    email: 'E-posta',
    password: 'Parola',
    login: {
      title: 'Giriş yap',
      submit: 'Giriş yap',
      noAccount: 'Hesabınız yok mu?',
      signupLink: 'Kayıt olun',
      forgot: 'Parolamı unuttum',
    },
    signup: {
      title: 'Hesap oluştur',
      name: 'Adınız',
      passwordHint: 'En az 10 karakter. Yaygın parolalar kabul edilmez.',
      terms: 'Kullanım koşullarını ve gizlilik bildirimini kabul ediyorum.',
      termsRequired: 'Devam etmek için koşulları kabul edin.',
      submit: 'Hesap oluştur',
      haveAccount: 'Zaten hesabınız var mı?',
      loginLink: 'Giriş yapın',
    },
    checkEmail: {
      title: 'E-postanızı kontrol edin',
      body: 'Doğrulama bağlantısını {{email}} adresine gönderdik. Bağlantı 24 saat geçerlidir.',
      bodyGeneric: 'Doğrulama bağlantısını e-posta adresinize gönderdik. Bağlantı 24 saat geçerlidir.',
      hint: 'E-posta gelmediyse istenmeyen klasörüne bakın. Bu adresle zaten bir hesabınız varsa giriş yapabilir ya da parolanızı sıfırlayabilirsiniz.',
      loginCta: 'Giriş sayfasına git',
    },
    verify: {
      title: 'E-posta doğrulama',
      working: 'Doğrulanıyor…',
      success: 'E-posta adresiniz doğrulandı.',
      continue: 'Devam et',
      loginCta: 'Giriş yap',
      missingToken: 'Bağlantıda doğrulama kodu yok. E-postadaki bağlantıyı eksiksiz açın.',
    },
    forgot: {
      title: 'Parolanızı mı unuttunuz?',
      body: 'E-posta adresinizi yazın; hesabınız varsa sıfırlama bağlantısı göndeririz.',
      submit: 'Bağlantı gönder',
      sent: 'Bu adrese ait bir hesap varsa sıfırlama bağlantısını gönderdik. Bağlantı 30 dakika geçerlidir.',
    },
    reset: {
      title: 'Yeni parola belirleyin',
      password: 'Yeni parola',
      confirm: 'Yeni parola (tekrar)',
      mismatch: 'Parolalar aynı değil.',
      submit: 'Parolayı değiştir',
      success: 'Parolanız değişti. Güvenliğiniz için bütün cihazlardaki oturumlar kapatıldı.',
      loginCta: 'Giriş yap',
      missingToken: 'Bağlantıda sıfırlama kodu yok. E-postadaki bağlantıyı eksiksiz açın.',
    },
    invite: {
      title: 'Ekip daveti',
      body: '“{{tenant}}” sizi {{role}} olarak davet etti.',
      forEmail: 'Davet şu adrese gönderildi: {{email}}',
      needLogin: 'Daveti kabul etmek için bu adresle giriş yapın ya da hesap oluşturun.',
      accept: 'Daveti kabul et',
      accepted: 'Ekibe katıldınız.',
      alreadyMember: 'Zaten bu ekibin üyesisiniz.',
      missingToken: 'Bağlantıda davet kodu yok. E-postadaki bağlantıyı eksiksiz açın.',
    },
    footer: 'Oppy · {{chatProduct}} ve {{codeProduct}}',
  },
  verifyBanner: {
    text: 'E-posta adresiniz henüz doğrulanmadı. Sohbet edebilirsiniz; davet göndermek ve dış bağlantılar için doğrulama gerekir.',
    resend: 'Bağlantıyı yeniden gönder',
    sent: 'Yeni doğrulama bağlantısı gönderildi.',
  },
  dashboard: {
    greeting: 'Merhaba, {{name}}',
    workspaces: {
      title: 'Çalışma alanları',
      newChat: 'Yeni sohbet alanı',
      newCode: 'Yeni kod çalışma alanı',
      name: 'Ad',
      empty: 'Henüz çalışma alanı yok.',
    },
    recent: { title: 'Son sohbetler', empty: 'Henüz sohbet yok.' },
    activity: {
      title: 'Şu an',
      waiting: 'Sizi bekleyenler',
      running: 'Çalışanlar',
      tasks: '{{count}} arka plan görevi çalışıyor.',
      kinds: { tool: 'Onay bekliyor', plan: 'Plan onayı bekliyor', question: 'Soru bekliyor' },
      status: { queued: 'Sırada', running: 'Çalışıyor', waiting_approval: 'Onay bekliyor', waiting_input: 'Yanıt bekliyor', waiting_children: 'Yardımcıları bekliyor' },
    },
    projects: { title: 'Projeler', empty: 'Henüz proje yok.' },
  },
  workspace: {
    tabsLabel: 'Çalışma alanı bölümleri',
    tabs: { conversations: 'Sohbetler', projects: 'Projeler', environment: 'Ortam', documents: 'Belgeler', skills: "Skill'ler", members: 'Üyeler', settings: 'Ayarlar' },
    documents: {
      title: 'Alan belgeleri',
      all: 'Tümü ({{count}})',
      add: 'Belge ekle ya da sürükleyin',
      uploading: 'Yükleniyor…',
      intro: 'Bu alanın üyelerinin sohbetlerinde kaynak olarak kullanılabilen belgeler.',
      empty: 'Henüz belge yok',
      emptyHint: 'Sözleşme, fiyat listesi, rapor… Eklediğiniz belgeler okunur ve bu alanın sohbetlerinde kaynak olur.',
    },
    invite: {
      button: 'Davet et',
      title: '{{name}} alanına davet et',
      add: 'Ekle',
      search: 'Firmada ara',
      person: 'Kişi',
      editorHint: 'Sohbet başlatır, belge ve skill ekler',
      viewerHint: 'Yalnız görür',
      nobody: 'Eklenebilecek kimse yok',
      nobodyHint: 'Firmanızdaki herkes bu alanda. Yeni birini önce firmaya davet edin.',
      toOrganization: 'Firmaya davet et',
    },
    skills: {
      intro: "Bu alanda kullanılabilen skill'ler; açıp kapatmanız yalnız bu alanda geçerli.",
      empty: "Bu alana özel skill yok",
      emptyHint: "Bir skill'i yalnız bu alanın üyeleri kullansın istiyorsanız buraya ekleyin.",
      own: "Bu alanın skill'leri",
      others: "Diğer skill'ler",
      toggle: "{{name}} skill'ini bu alanda aç ya da kapat",
    },
    settings: {
      name: 'Ad',
      directoryName: 'Bu ortak alanın adını firmanın yöneticileri Yönetim → Kimlik doğrulama\'dan verir.',
      description: 'Açıklama',
      descriptionHint: 'Adın altında görünür.',
      saved: 'Kaydedildi',
      deleteTitle: 'Çalışma alanını sil',
      deleteHint: 'Alan; sohbetleri, projeleri ve belgeleriyle birlikte silinir.',
      delete: 'Çalışma alanını sil',
      deleteConfirmTitle: '{{name}} silinsin mi?',
    },
    actions: 'Çalışma alanı işlemleri',
    deleteConfirm: 'Sohbetleri, projeleri ve belgeleri de görünmez olur.',
    conversations: {
      title: 'Sohbetler',
      new: 'Yeni sohbet',
      empty: 'Henüz sohbet yok',
      emptyHint: 'Bir soru sorun ya da bir belge ekleyin; bu alandaki sohbetleriniz burada toplanır. Sohbetler yalnız size görünür.',
      search: 'Bu alandaki sohbetlerde ara',
      allProjects: 'Tüm projeler',
    },
    projects: {
      codeHint: 'Projeler, kod tabanınızdaki klasörlerdir; bir sohbeti bir klasörde başlatırsınız.',
      emptyHint: 'Projeler sohbetleri ve belgeleri bir iş etrafında toplar.',
      emptyShort: 'Henüz proje yok.',
      title: 'Projeler',
      new: 'Yeni proje',
      name: 'Proje adı',
      rootPath: 'Klasör',
      rootPathHint: 'Çalışma alanındaki klasör (ör. web ya da services/api). Boş bırakırsanız addan üretilir. İç içe klasörler ayrı proje olamaz.',
      chatHint: 'Projeler sohbetleri ve belgeleri gruplar.',
      empty: 'Henüz proje yok.',
    },
    members: {
      intro: "Üyeler alanın belgelerini ve skill'lerini paylaşır; sohbetler kişiye özeldir.",
      removeTitle: '{{name}} alandan çıkarılsın mı?',
      removeBody: "Alanın belgelerine ve skill'lerine erişemez; kendi sohbetleri ona kalır.",
      title: 'Üyeler',
      add: 'Üye ekle',
      person: 'Kişi',
      role: 'Rol',
      remove: 'Çıkar',
      noCandidates: 'Eklenecek başka ekip üyesi yok.',
    },
  },
  conversation: {
    // D-152
    personalWorkspace: 'Kişisel kod alanınız açıldı.',
    options: "Sohbet seçenekleri: düşünme, skill'ler, dışa aktarma",
    placeholderQuestion: 'Soruyu yukarıdan yanıtlayın ya da buraya yazın…',
    untitled: 'Adsız sohbet',
    placeholder: 'Mesajınızı yazın…',
    send: 'Gönder',
    empty: 'Henüz mesaj yok. Bir şey yazarak başlayın.',
    you: 'Siz',
    assistant: 'Oppy',
    streaming: 'Yanıt yazılıyor…',
    connected: 'Bağlı',
    reconnecting: 'Bağlantı koptu, yeniden bağlanıyor…',
    turnFailed: 'Yanıt üretilemedi. Yeniden deneyin.',
    turnCancelled: 'Yanıt durduruldu.',
    backToWorkspace: 'Çalışma alanına dön',
    attach: 'Dosya ekle',
    attaching: 'Yükleniyor…',
    removeAttachment: 'Eki kaldır',
    attachmentLimit: 'Dosyalar en çok {{mb}} MB olabilir.',
    dropHere: 'Dosyaları buraya bırakın',
    menu: 'Konuşma menüsü',
    tool: {
      running: 'Çalışıyor',
      done: 'Tamamlandı',
      failed: 'Tamamlanamadı',
      details: 'Teknik ayrıntılar',
      output: 'Çıktı',
      result: 'Sonuç',
      seconds: '{{seconds}} sn',
    },
    artifact: {
      download: 'İndir',
      preview: 'Önizle',
      version: 'sürüm {{version}}',
      kinds: {
        image: 'Görsel',
        document: 'Word belgesi',
        spreadsheet: 'Excel tablosu',
        presentation: 'Sunum',
        pdf: 'PDF',
        csv: 'CSV',
        html: 'Web sayfası',
        markdown: 'Metin',
        text: 'Metin',
        archive: 'Arşiv',
        other: 'Dosya',
      },
    },
    queue: 'Sıraya ekle',
    emptyCode: 'Bir görev verin: plan, dosyalar ve komutlar burada görünür.',
    answerReady: 'Yanıt hazır.',
    toBottom: 'En alta in',
    connecting: 'Bağlanıyor…',
    turnsLabel: 'Konuşmanın mesajları',
  },
  agent: {
    continue: 'Devam et',
    stop: 'Durdur',
    queued: 'Sırada',
    reasoning: 'Düşünce',
    status: {
      waiting: 'Onayınızı bekliyor',
      retry: 'Model yanıt vermedi; yeniden deneniyor ({{attempt}}/{{max}})…',
      compacting: 'Konuşma özetleniyor…',
      retryIn: '(sonraki deneme {{seconds}} sn içinde; isterseniz durdurup başka model seçebilirsiniz)',
      thinking: 'Düşünüyor…',
    },
    approval: {
      waiting: 'Onay bekleyen işlem',
      explain: 'Açıkla',
      fromHelper: 'Bir yardımcı istiyor: {{description}}',
      approved: 'onaylandı ({{option}})',
      answered: 'yanıtlandı',
      rejected: 'reddettiniz',
      expired: 'süre doldu; ajan yanıtsız devam etti',
      cancelled: 'iptal edildi',
      pending: 'bekliyor',
    },
    sent: {
      source: 'Bağlantı: {{name}} · araç: {{tool}}',
      intro: 'Oppy şunları gönderecek:',
      introRemember: 'Oppy hafızasına şunu kaydedecek:',
      introOwn: 'Oppy şunları kullanacak:',
      raw: 'Ham veriyi göster',
      more: 'Tamamını göster',
      less: 'Daha az göster',
      empty: 'boş',
      fields_one: '{{count}} alan',
      fields_other: '{{count}} alan',
      cut_one: '… {{chars}} karakter daha (gösterilmiyor)',
      cut_other: '… {{chars}} karakter daha (gösterilmiyor)',
      truncated: 'Gönderilecek veri çok uzun: kartta {{total}} karakterin {{shown}} karakteri gösteriliyor. Kısaltılan metinlerin sonunda kaç karakterin gösterilmediği yazıyor. İzin vermeden önce bunu göz önünde bulundurun.',
    },
    question: {
      heading_one: 'Bir sorum var',
      heading_other: '{{count}} sorum var',
      intro: 'Yanıtlarsanız hemen devam ederim. Bilmediğinizi boş bırakabilirsiniz.',
      otherPlaceholder: 'Ya da kendiniz yazın',
      title: 'Soru',
      other: 'Kendi yanıtınız',
      submit: 'Yanıtla',
      skip: 'Atla',
      expires: 'Yanıt gelmezse saat {{time}} olunca boş bırakılarak devam edilir',
    },
    plan: {
      title: 'Plan',
      keptPlanning: 'planlamaya devam edildi',
      edit: 'Düzenle',
      preview: 'Önizle',
      feedback: 'Not ekleyin (planlamaya devam derseniz ajana iletilir)',
      approved: 'Plan onaylandı',
      approvedEdited: 'Plan düzenlenerek onaylandı',
    },
    todos: {
      progress: 'Plan ilerlemesi',
      running: 'sürüyor',
      title: 'Görevler ({{done}}/{{total}})',
      waits: '({{ids}} bekleniyor)',
      step: 'Adım {{current}} / {{total}}',
    },
    helper: {
      group: '{{count}} yardımcı birlikte çalışıyor',
      background: 'Arka planda',
      usage: '{{tokens}} token · {{calls}} model çağrısı · {{tools}} araç · {{seconds}} sn',
      cost: 'maliyet {{cost}}',
      steps: '{{count}} adım',
      result: 'Sonuç',
      status: { running: 'Çalışıyor', completed: 'Bitti', failed: 'Bitmedi', cancelled: 'Durduruldu', interrupted: 'Kesildi' },
    },
    tasks: {
      title: 'Arka plan görevleri ({{running}} çalışıyor, toplam {{total}})',
      stop: 'Durdur',
      status: { running: 'Çalışıyor', completed: 'Bitti', failed: 'Hata verdi', cancelled: 'Durduruldu', stopped_by_restart: 'Yeniden başlatmayla durdu' },
    },
    mode: {
      fullTitle: 'Tam erişim açılsın mı?',
      fullOk: 'Tam erişimi aç',
      label: 'Onay kipi',
      chip: 'Mod: {{mode}}',
      ask: 'Sor',
      auto_edit: 'Düzenlemeleri sorma',
      plan: 'Plan',
      full: 'Tam erişim',
      hints: {
        ask: 'Dosya değiştiren ve komut çalıştıran her işlemde onayınız istenir.',
        auto_edit: 'Dosya düzenlemeleri sorulmadan yapılır; komutlar yine sorulur.',
        plan: 'Ajan yalnız okur ve bir plan hazırlar; onayınıza kadar değişiklik yapmaz.',
        full: 'Ajan sormadan komut çalıştırır ve dosya değiştirir.',
      },
      fullConfirm: 'Tam erişimde ajan sormadan komut çalıştırır ve dosya değiştirir.',
    },
    notice: {
      failed: 'Yanıt üretilemedi. Yeniden deneyin.',
      cancelled: 'Durdurdunuz. Yazılan dosyalar ve gösterilen yanıt duruyor.',
      rejected: 'Reddettiniz; ajan bu işlemi yapmadı.',
      interrupted: 'Çalışma yarıda kesildi; bir işlemin sonucu bilinmiyor.',
      run_interrupted: 'Sunucu yeniden başladı; çalışma kaldığı yerden sürdürülüyor…',
      run_resumed: 'Çalışma kaldığı yerden sürüyor.',
      limit: {
        tool_calls: 'Bu turda {{max}} araç çağrısı sınırına ulaşıldı (döngü koruması).',
        steps: 'Bu turda {{max}} adım sınırına ulaşıldı.',
        repeat: 'Ajan aynı işlemi art arda denedi; durduruldu.',
      },
    },
    notification: 'Arka plan görevi bitti: {{label}}',
    notify: {
      enable: 'Bildirimleri aç',
      question: 'Oppy size bir soru soruyor',
      approval: 'Oppy onayınızı bekliyor',
    },
    preview: { autoRefresh: 'Dosyalar değişince yenile' },
    rules: {
      title: 'İzin kuralları',
      hint: 'Onay kartında "hep izin ver" dediğiniz işlemler. Kaldırdığınızda yeniden sorulur.',
      empty: 'Henüz kalıcı izin yok.',
      revoke: 'Kaldır',
      scopes: { user: 'Bütün çalışma alanlarım', workspace: 'Bir çalışma alanı', conversation: 'Bir konuşma' },
    },
    fullAccess: {
      title: 'Tam erişim',
      hint: 'Açıksa üyeler güvenilir kod çalışma alanlarında tam erişim kipini seçebilir; ajan sormadan komut çalıştırır.',
      enable: 'Tam erişime izin ver',
    },
  },
  settings: {
    defaultModel: {
      title: 'Varsayılan modelim',
      hint: 'Yeni konuşmalarınız bu modelle başlar. Boş bırakırsanız firmanın seçimi geçerli olur.',
    },
    providers: { hint: 'Kendi sağlayıcılarınızı yalnız siz görür ve kullanırsınız; anahtarlar şifreli saklanır ve bir daha gösterilmez.' },
    title: 'Ayarlar',
    profile: {
      title: 'Profil',
      name: 'Adınız',
      language: 'Arayüz dili',
      userType: 'Kullanım biçimi',
      business: 'Belgeler ve araştırma',
      developer: 'Kod ve projeler',
    },
    email: { title: 'E-posta', verified: 'Doğrulandı', unverified: 'Doğrulanmadı' },
    password: {
      title: 'Parola',
      directory: 'Parolanızı şirketinizin BT ekibi yönetir; Oppy parolanızı görmez ve saklamaz.',
      current: 'Mevcut parola',
      next: 'Yeni parola',
      submit: 'Parolayı değiştir',
      success: 'Parolanız değişti. Diğer cihazlardaki oturumlar kapatıldı.',
    },
    sessions: {
      title: 'Açık oturumlar',
      current: 'Bu cihaz',
      lastUsed: 'Son kullanım: {{date}}',
      revoke: 'Oturumu kapat',
      unknownDevice: 'Bilinmeyen cihaz',
      app: 'Oppy uygulaması',
    },
    answers: {
      title: 'Yanıtlar',
      language: 'Yanıt dili',
      languageHint: 'Otomatik seçilirse yanıt, mesajınızın dilinde gelir.',
      auto: 'Otomatik',
      instructions: 'Her konuşmaya eklenecek talimatlarınız',
      instructionsHint: 'Ör. Kısa yaz, rakamları tabloyla göster. En çok 4000 karakter.',
      timeZone: 'Saat dilimi',
      timeZoneHint: 'Asistan bugünün tarihini ve mesajlarınızın saatini bu dilime göre görür. Firmanın varsayılanı: {{zone}}.',
      timeZoneAuto: 'Tarayıcıya göre ({{zone}})',
    },
    data: {
      title: 'Verileriniz',
      exportHint: 'Bu firmadaki bütün konuşmalarınızı, hafızanızı ve tercihlerinizi tek bir dosya olarak indirin.',
      export: 'Verilerimi indir',
      deleteTitle: 'Verilerimi sil',
      deleteHint: 'Bu firmadaki bütün konuşmalarınız, yüklediğiniz ve üretilen dosyalar, çalışma ortamlarınız, hafızanız ve bildirimleriniz kalıcı olarak silinir; varsa Microsoft bağlantınız da kaldırılır. Hesabınız ve ekibin ortak çalışma alanları kalır.',
      deleted: '{{conversations}} konuşma, {{memories}} hafıza kaydı ve {{documents}} belge silindi.',
      confirm: 'Bunun geri alınamayacağını anlıyorum.',
      delete: 'Verilerimi kalıcı olarak sil',
      email: 'E-posta adresiniz',
      emailHint: 'Şirket hesabınızın burada parolası yok. Onaylamak için hesabınızın e-posta adresini yazın: {{email}}',
    },
  },
  // Phase 13d: Yönetim → "Kimlik doğrulama"
  identity: {
    none: 'Bu sürümde şirket kimlik servisiyle giriş kullanılmıyor.',
    connection: {
      title: 'Bağlantı',
      hint: 'Şirketin kimlik servisi. Bu değerler sunucu ayarlarından gelir; buradan değiştirilmez.',
      kind: 'Tür',
      companyAuth: 'Şirket kimlik servisi (Active Directory, Keycloak)',
      address: 'Adres',
      issuer: 'Beklenen yayıncı (iss)',
      audience: 'Beklenen istemci (aud, azp)',
      unchecked: 'denetlenmiyor',
      ownerRole: 'Süper yönetici rolü',
      adminRole: 'Ekip yöneticisi rolü',
      roleFields: 'Rollerin okunduğu alanlar',
      domainFields: "Domain'lerin okunduğu alanlar",
      prefix: 'ön ek: {{prefix}}',
      fallback: 'İlk kurulum yedeği',
      fallbackOn: 'Açık: OPPY_ADMIN_USERNAMES ({{count}})',
      fallbackOff: 'Kapalı',
      test: 'Bağlantıyı test et',
      result: { reachable: 'Servis yanıt veriyor', unexpected: 'Beklenmeyen yanıt', unreachable: 'Servise ulaşılamıyor' },
      answer: 'HTTP {{status}}, {{ms}} ms',
      noAnswer: '{{ms}} ms içinde yanıt yok',
    },
    domains: {
      title: "Domain'ler",
      hint: "Şirketin dizinindeki ekipler. Her domain'in ortak bir {{chatProduct}} çalışma alanı vardır; adını buradan verirsiniz. Üyelik dizinden gelir.",
      empty: "Henüz domain yok. Bir domain'den biri ilk kez giriş yaptığında burada görünür.",
      domain: 'Domain',
      workspace: 'Çalışma alanının adı',
      members: 'Üye',
      admins: 'Yönetici',
      displayName: '{{domain}} çalışma alanının adı',
    },
  },
  team: {
    handover: {
      text: '{{name}} çıkarılmadan önce çalışma alanları ve içlerindeki dosyalar kime geçsin?',
      leaveText: 'Ayrılmadan önce çalışma alanlarınız ve içlerindeki dosyalar kime geçsin?',
      to: 'Devralacak kişi',
      private: 'Özel sohbetler devredilmez: çöp kutusuna taşınır ve saklama süresi dolunca silinir.',
      copies: 'Devralan kişinin aynı kod çalışma alanında kendi kopyası varsa, ayrılanın kopyası silinir.',
    },
    title: 'Ekip',
    name: 'Firmanın adı',
    nameSuperAdminOnly: 'Firmanın adını yalnız süper yönetici değiştirir.',
    members: { title: 'Üyeler', remove: 'Çıkar', leave: 'Ayrıl', role: 'Rol', directory: 'Şirket hesabı', deactivated: 'Erişimi kapalı', deactivate: 'Erişimi kapat', reactivate: 'Erişimi aç' },
    directoryHint: 'Kişiler şirket hesaplarıyla ilk kez giriş yaptıklarında firmaya katılır. Rolleri ve domain\'leri şirketin dizininden gelir; davet gönderilmez.',
    invite: {
      title: 'Davet et',
      role: 'Rol',
      submit: 'Davet gönder',
      sent: 'Davet gönderildi.',
      needVerified: 'Davet göndermek için önce e-posta adresinizi doğrulayın.',
      onlyAdmins: 'Yalnız süper yöneticiler ve yöneticiler üye davet edebilir.',
    },
    invites: {
      title: 'Bekleyen davetler',
      expires: 'Son geçerlilik: {{date}}',
      revoke: 'Geri al',
      empty: 'Bekleyen davet yok.',
    },
    switcher: 'Firma değiştir',
    switches: {
      title: 'Firma ayarları',
      memory: 'Hafızaya izin ver',
      memoryHint: 'Kapalıysa kimsenin hafızası yanıtlara katılmaz ve yeni bilgi kaydedilmez.',
      codeApproval: 'Sohbette kod çalıştırmadan önce onay iste',
      codeApprovalHint: 'Kapalıyken kod, sohbetin internetsiz ve özel ortamında sorulmadan çalışır; kullanıcı ne yapıldığını görür.',
      trash: 'Çöp kutusu saklama süresi (gün)',
      trashHint: 'Boş bırakılırsa 30 gün.',
      timeZone: 'Firmanın saat dilimi',
      timeZoneHint: 'Firmanın günü bu dilimde 00:00’da döner: günlük bütçeler o saatte yenilenir, “Bugün” ve “N gün önce” buna göre sayılır. Tarayıcısı saat dilimini bildirmeyen ve kendisi seçmeyen kişiler de bu dilimi kullanır.',
    },
    feedback: {
      title: 'Yanıt değerlendirmeleri',
      hint: 'Son 30 gün; model başına beğeni sayıları. Konuşma içerikleri görünmez.',
      empty: 'Henüz değerlendirme yok.',
      notes: '{{count}} not',
      columns: { model: 'Model', up: 'Beğeni', down: 'Beğenmeme', notes: 'Notlu' },
    },
  },
  sandbox: {
    title: 'Çalışma ortamı',
    states: { running: 'Çalışıyor', idle: 'Boşta', suspended: 'Askıda', missing: 'Henüz açılmadı' },
    start: 'Başlat',
    suspend: 'Askıya al',
    isolated: 'Size özel, yalıtılmış ortam. Diğer üyeler kendi ortamlarını kullanır.',
    tabs: { files: 'Dosyalar', terminal: 'Terminal', git: 'Git', preview: 'Önizleme' },
    files: {
      root: 'Çalışma alanı',
      up: 'Üst klasör',
      empty: 'Bu klasör boş.',
      hiddenIgnored: '{{count}} öge .gitignore nedeniyle gizli.',
      upload: 'Dosya yükle',
      download: 'İndir',
      delete: 'Çöpe taşı',
      zip: 'ZIP olarak indir',
      trash: 'Çöp kutusu',
      trashEmpty: 'Çöp kutusu boş.',
      restore: 'Geri getir',
      close: 'Kapat',
      truncated: 'Dosyanın yalnız bir bölümü gösteriliyor.',
    },
    terminal: {
      connect: 'Terminali aç',
      disconnect: 'Kapat',
      closed: 'Terminal kapandı.',
      hint: 'Kabuk çalışma alanı klasöründe açılır; platform anahtarları bu ortamda yoktur.',
    },
    git: {
      notRepo: 'Bu çalışma alanında Git deposu yok.',
      init: 'Git deposu oluştur',
      branch: 'Dal: {{branch}}',
      clean: 'Değişiklik yok.',
      message: 'Commit mesajı',
      commit: 'Commit',
      selectAll: 'Hepsini seç',
      diff: 'Farkı göster',
      committed: 'Commit oluşturuldu.',
    },
    preview: {
      ports: 'Çalışan uygulamalar',
      noPorts: 'Dinleyen bir port yok. Uygulamanızı çalıştırınca burada görünür.',
      refresh: 'Yenile',
      open: 'Önizlemeyi aç',
      newTab: 'Yeni sekmede aç',
      reload: 'Sayfayı yenile',
      frameTitle: 'Uygulama önizlemesi',
      // v0.15a.3: an installation reached by its IP address
      offTitle: 'Önizleme bu kurulumda kapalı',
      offBody: 'Alan adı bağlanınca açılır. Dosyalarınız Dosyalar sekmesinde; oradan açıp indirebilirsiniz.',
      offFiles: 'Dosyalar',
    },
    panel: 'Çalışma alanı paneli',
    tabsLabel: 'Panel sekmeleri',
    offTitle: 'Çalışma ortamı kapalı',
  },
  // D-99: CompanyCode's panel next to a conversation (KodPanel.dc.html, KodPanelKapali.dc.html)
  codePanel: {
    hide: 'Paneli gizle',
    show: 'Paneli göster',
    showRunning: 'Paneli göster · çalışma alanı çalışıyor',
    showRunningLabel: 'Paneli göster. Çalışma alanı çalışıyor',
    width: 'Panel genişliği',
    openFile: 'Panelde aç: {{path}}',
    previewReady: 'Uygulama {{port}} portunda çalışıyor',
    openPreview: 'Önizlemede aç',
    previewOff: 'Uygulama {{port}} portunda çalışıyor; bu kurulumda önizleme kapalı',
    showFiles: 'Dosyaları göster',
  },
  experience: {
    chat: '{{chatProduct}}',
    code: '{{codeProduct}}',
    toCode: '{{codeProductTo}} geç',
    toChat: '{{chatProductTo}} geç',
  },
  notifications: {
    title: 'Bildirimler',
    readAll: 'Hepsini okundu say',
    empty: 'Bildirim yok.',
    kinds: {
      approval: 'Onay bekliyor',
      question: 'Soru',
      task: 'Arka plan',
      run_failed: 'Yanıt alınamadı',
      run_interrupted: 'Yarıda kaldı',
      license: 'Lisans',
    },
    titleUnread_one: 'Bildirimler: {{count}} okunmamış',
    titleUnread_other: 'Bildirimler: {{count}} okunmamış',
  },
  sessions: {
    closeSearch: 'Aramayı kapat',
    viewAll: 'Tümünü gör',
    historyTitle: 'Sohbet geçmişi',
    purgeTitle: 'Sohbet kalıcı olarak silinsin mi?',
    day: { today: 'Bugün', yesterday: 'Dün', week: 'Son 7 gün', month: 'Son 30 gün', older: 'Daha eski' },
    title: 'Konuşmalar',
    actions: 'Konuşma işlemleri',
    restore: 'Geri getir',
    purge: 'Kalıcı olarak sil',
    purgeConfirm: 'Dosyaları ve ürettiği dosyalarla birlikte silinir. Geri alınamaz.',
    pin: 'Sabitle',
    unpin: 'Sabitlemeyi kaldır',
    pinned: 'Sabitlenmiş',
    group: 'Grup',
    noGroup: 'Grupsuz',
    archive: 'Arşivle',
    unarchive: 'Arşivden çıkar',
    toTrash: 'Çöp kutusuna taşı',
    allGroups: 'Tümü',
    newGroup: 'Yeni grup',
    deleteGroup: 'Grubu sil (konuşmalar kalır)',
    newChat: 'Yeni sohbet',
    newCode: 'Yeni kod konuşması',
    search: 'Konuşmalarda ara',
    noResults: 'Eşleşen konuşma yok.',
    views: 'Görünüm',
    view: { active: 'Etkin', archived: 'Arşiv', trash: 'Çöp kutusu' },
    trashHint: 'Çöp kutusundaki konuşmalar saklama süresi dolunca kendiliğinden silinir.',
    emptyTrash: 'Çöp kutusunu boşalt',
    emptyTrashConfirm: 'Çöp kutusundaki bütün sohbetler kalıcı olarak silinir. Geri alınamaz.',
    pinnedTitle: 'Sabitlenmiş',
    recentTitle: 'Son konuşmalar',
    empty: { active: 'Henüz konuşma yok.', archived: 'Arşivde konuşma yok.', trash: 'Çöp kutusu boş.' },
    more: 'Daha fazla göster',
    count: '{{count}} konuşma',
    options: 'Geçmiş seçenekleri',
  },
  status: { answer: 'Yanıtınızı bekliyor', approval: 'Onayınızı bekliyor', running: 'Çalışıyor' },
  time: { now: 'az önce', minutes: '{{count}} dk önce', hours: '{{count}} sa önce', today: 'Bugün {{time}}', yesterday: 'Dün', yesterdayAt: 'Dün {{time}}' },
  attach: {
    label: 'Ekler',
    uploading: 'Yükleniyor',
    reading: 'Okunuyor',
    ready: 'Hazır',
    readyWith: 'Hazır · {{size}}',
    ocr: 'metin görüntüden tanındı',
    failed: 'Yüklenemedi',
    unreadable: 'Okunamadı: {{reason}}',
    duplicate: 'Bu dosya zaten ekli',
    remove: '{{name}} ekini kaldır',
  },
  steps: {
    label: 'Adımlar',
    allowedBy: {
      trusted_read: 'güvenilir bağlantı, salt okunur: sorulmadı',
      tool_policy: 'yönetici izin verdi: sorulmadı',
      rule: 'kalıcı izninizle: sorulmadı',
      full_access: 'tam erişim: sorulmadı',
    },
    show: 'Adımları göster',
    hide: 'Adımları gizle',
    outcome: {
      prepared: "{{time}}'de hazırlandı",
      preparedNoTime: 'Hazırlandı',
      stopped: 'Durduruldu · {{time}}',
      stoppedNoTime: 'Durduruldu',
      budget: 'Bütçe dolduğu için durdu · {{time}}',
      budgetNoTime: 'Bütçe dolduğu için durdu',
      allowance: 'Günlük hak dolduğu için bekliyor · {{time}}',
      allowanceNoTime: 'Günlük hak dolduğu için bekliyor',
      failed: 'Tamamlanamadı · {{time}}',
      failedNoTime: 'Tamamlanamadı',
      ended: 'Durdu · {{time}}',
      endedNoTime: 'Durdu',
    },
    manySkills: "Bu turda {{count}} skill yüklendi; gerekmeyen skill'ler yanıtı yavaşlatır.",
    docWaitFailed: 'Belge okunamadı: {{names}}',
    skill: {
      byUser: '/ ile siz çalıştırdınız',
      reused: 'Zaten yüklüydü; yeniden eklenmedi',
      part: 'Parça {{part}}/{{parts}}',
      grants: 'Bu tur sorulmadan: {{list}}',
      ownScripts: "skill'in kendi betikleri",
    },
    think: { examine: 'Soruyu inceledi', evaluate: 'Bulguları değerlendirdi' },
    answer: 'Yanıt yazıldı',
    failed: 'tamamlanamadı',
    results: 'Arama sonuçları',
    moreResults: '+{{count}} sonuç',
    thought: 'Düşünce',
    current: {
      waitingAnswer: 'Yanıtınızı bekliyor',
      examine: 'Soruyu inceliyor',
      evaluate: 'Bulguları değerlendiriyor',
      writing: 'Yanıt yazılıyor',
      waiting: 'Onayınızı bekliyor',
      compacting: 'Konuşma özetleniyor',
      retry: 'Yeniden deneniyor ({{attempt}}/{{max}})',
    },
    parts: {
      skills_one: '{{count}} skill',
      skills_other: '{{count}} skill',
      docs_one: '{{count}} belge araması',
      docs_other: '{{count}} belge araması',
      searches_one: '{{count}} arama',
      searches_other: '{{count}} arama',
      pages_one: '{{count}} sayfa',
      pages_other: '{{count}} sayfa',
      files_one: '{{count}} dosya',
      files_other: '{{count}} dosya',
      commands_one: '{{count}} komut',
      commands_other: '{{count}} komut',
      other_one: '{{count}} işlem',
      other_other: '{{count}} işlem',
    },
    tools: {
      web_search: { running: "Web'de aranıyor:", done: "Web'de arandı:" },
      web_fetch: { running: 'Okunuyor:', done: 'Okundu:' },
      doc_search: { running: 'Belgelerde aranıyor:', done: 'Belgelerde arandı:' },
      doc_read: { running: 'Belge okunuyor:', done: 'Belge okundu:' },
      skill: { running: 'Skill yükleniyor:', done: 'Skill yüklendi:' },
      doc_wait: { running: 'Belge okunuyor:', done: 'Belge okundu:' },
      skill_search: { running: 'Skill aranıyor:', done: 'Skill arandı:' },
      agent: { running: 'Yardımcı çalışıyor:', done: 'Yardımcı bitirdi:' },
      write_file: { running: 'Dosya yazılıyor:', done: 'Dosya yazıldı:' },
      append_file: { running: 'Dosyaya ekleniyor:', done: 'Dosyaya eklendi:' },
      edit: { running: 'Dosya düzenleniyor:', done: 'Dosya düzenlendi:' },
      read_file: { running: 'Dosya okunuyor:', done: 'Dosya okundu:' },
      ls: { running: 'Klasöre bakılıyor:', done: 'Klasöre bakıldı:' },
      glob: { running: 'Dosya aranıyor:', done: 'Dosya arandı:' },
      grep: { running: 'Metin aranıyor:', done: 'Metin arandı:' },
      shell: { running: 'Komut çalışıyor:', done: 'Komut çalıştı:' },
      ...m365StepToolsTr,
    },
    unreadable: { one: 'Okunamadı: {{host}} · {{reason}}', many: 'Okunamadı: {{count}} site {{reason}}' },
    reasons: {
      closed: 'otomatik okumaya kapalı',
      down: 'site kapalı',
      timeout: 'yanıt vermedi',
      notFound: 'sayfa bulunamadı',
      serverError: 'site hata verdi',
      invalid: 'adres geçersiz',
      blocked: 'erişime kapalı',
      redirects: 'yönlendirme döngüsü',
      other: 'okunamadı',
    },
    time: { seconds: '{{value}} sn', minutes: '{{minutes}} dk {{seconds}} sn' },
  },
  markdown: { copy: 'Kopyala', copied: 'Kopyalandı' },
  usage: {
    turn: '↑ {{input}} · ↓ {{output}} token · {{calls}} model çağrısı',
    period: 'Dönem',
    days_one: 'Son {{count}} gün',
    days_other: 'Son {{count}} gün',
    seconds_one: '{{count}} sn',
    seconds_other: '{{count}} sn',
    minutes_one: '{{count}} dk',
    minutes_other: '{{count}} dk',
    hours: '{{hours}} sa {{minutes}} dk',
    empty: 'Bu dönemde kullanım yok.',
    byMember: 'Üyelere göre kullanım',
    byWorkspace: 'Çalışma alanlarına göre kullanım',
    columns: {
      members: 'Üye',
      workspaces: 'Çalışma alanı',
      input: 'Giriş token',
      output: 'Çıkış token',
      calls: 'Model çağrısı',
      runs: 'Çalışma',
      sandbox: 'Kod çalıştırma süresi',
      storage: 'Depolama',
      cost: 'Maliyet',
    },
    unlimited: '{{used}} kullanıldı · sınır yok',
    percent: '%{{percent}} kullanıldı',
    warning: 'Sınıra yaklaştı',
    full: 'Doldu',
    budgets: {
      domain: 'Domain',
      allDomains: "Tüm domain'ler",
      domainHint: 'Firmanın bütçelerini yöneticiler ayarlar. Siz domain\'lerinizdeki üyelerin günlük bütçesini firmanın günlük bütçesini aşmadan ayarlarsınız.',
      title: 'Token bütçeleri',
      hint: 'Bütçe dolunca yeni mesaj gönderilemez; kişiye ne zaman yenileneceği söylenir ve burada bir kayıt düşer. %80’den sonra uyarı gösterilir.',
      monthly: 'Firmanın bu ayki kullanımı',
      monthlyLimit: 'Aylık firma bütçesi (token)',
      emptyUnlimited: 'Boş bırakırsanız sınır olmaz.',
      daily: 'Üye başına günlük bütçe (istem token’ı)',
      dailyHint: 'Boşsa platformun değeri geçerli: iş kullanıcısı {{business}}, yazılımcı {{developer}}.',
      today: 'Bugün',
      own: 'Kişiye özel günlük bütçe',
      memberOwn: '{{name}} için günlük bütçe',
      inherit: 'Boş: firmanın değeri ({{value}})',
      noLimit: 'Sınır yok',
      platformValue: 'Platformun değeri',
      fullToday_one: 'Bugün {{count}} üyenin bütçesi doldu.',
      fullToday_other: 'Bugün {{count}} üyenin bütçesi doldu.',
      resetsDaily: 'Günlük bütçeler her gece {{time}}’{{ending}} yenilenir.',
    },
  },
  // the context dialog (BaglamNormal, BaglamDolu; D-118)
  context: {
    label: 'Bağlam: yüzde {{percent}} dolu',
    title: 'Bağlam',
    used: '{{used}} / {{window}}',
    estimated: 'Tahmini; ilk yanıttan sonra kesinleşir.',
    barLabel: 'Bağlam: {{percent}} dolu',
    status: {
      ok: 'Bol yer var · {{auto}}’{{ending}} otomatik özetlenir',
      warn: '<b>Dolmak üzere</b> · {{left}} sonra otomatik özetlenir',
      high: '<b>Doldu</b> · bir sonraki mesajda otomatik özetlenir',
    },
    about: {
      system: 'Oppy’nin temel yönergeleri',
      systemWith: 'Oppy’nin temel yönergeleri; ayrıca {{list}}',
      tools: 'Web araması, dosya okuma gibi araçların tanımı',
      skills: 'Açık skill’lerin adları ve açıklamaları',
      skillsLoaded: 'Açık skill’lerin adları ve açıklamaları; yüklü: {{list}}',
      environment: 'Tarih, dil ve çalışma alanı bilgisi',
      messages: 'Bu konuşmadaki yazışmalar, belgeler ve araç sonuçları',
    },
    extras: { project: 'proje talimatı', instructions: 'kendi talimatlarınız', memory: 'hafıza' },
    session_one: 'Bu sohbette: {{input}} girdi · {{output}} çıktı · {{calls}} çağrı',
    session_other: 'Bu sohbette: {{input}} girdi · {{output}} çıktı · {{calls}} çağrı',
    compactHint: 'Eski mesajlar kısa bir özete dönüşür, konuşma sürer.',
    compact: 'Şimdi özetle',
    newConversation: 'Yeni sohbet',
    categories: {
      system: 'Sistem talimatı',
      tools: 'Araçlar',
      skills: "Skill'ler",
      environment: 'Ortam',
      messages: 'Mesajlar',
    },
  },

  model: {
    defaultOption: 'Varsayılan model',
    defaultShort: 'Varsayılan',
    isDefaultLower: 'varsayılan model',
    thinkingShort: 'Zor sorularda önce düşünür',
    label: 'Bu konuşmanın modeli',
    hint: 'Yalnız bu konuşmayı değiştirir; varsayılan modeliniz aynı kalır.',
    default: 'Varsayılan ({{name}})',
    notAllowed: 'firmanızda izinli değil',
    thinking: 'Düşünme',
    thinkingHint: 'Kapalıyken model yanıttan önce uzun uzun düşünmez; yanıt daha hızlı gelir.',
    makeDefault: 'Varsayılanım yap',
    isDefault: 'Varsayılanınız oldu',
  },
  rewind: {
    title: 'Geri sar',
    hint: 'Seçtiğiniz mesaj ve sonrası konuşmadan çıkar; mesaj düzenlemeniz için yazma alanına döner.',
    none: 'Geri sarılabilecek mesaj yok.',
    attachmentsOnly: '(yalnız ek)',
    removes: 'Konuşmadan {{count}} mesaj çıkacak.',
    what: 'Neler geri alınsın?',
    conversationOnly: 'Yalnız konuşma',
    withFiles: 'Konuşma ve dosyalar',
    noFileChanges: 'Bu noktadan sonra dosya değişmedi.',
    status: { added: 'Silinecek', modified: 'Eski hâline dönecek', deleted: 'Geri gelecek', type_changed: 'Eski hâline dönecek' },
    confirm: 'Geri sar',
  },
  message: {
    edit: 'Düzenle',
    rewind: 'Buraya geri sar',
    editHint: 'Gönderdiğinizde bu mesaj ve sonrası yenisiyle değişir.',
    editSend: 'Düzenleyip gönder',
    good: 'İyi yanıt',
    bad: 'Kötü yanıt',
    badNote: 'Neyi beğenmediniz? (isteğe bağlı)',
    sendFeedback: 'Gönder',
    regenerate: 'Yeniden üret',
    regenerateModel: 'Yeniden üretirken kullanılacak model',
    sameModel: 'Aynı model',
    regenerateWith: 'Yeniden üret',
  },
  compaction: {
    auto: 'Konuşma uzadığı için önceki kısmı özetlendi',
    manual: 'Konuşma özetlendi',
    tokens: '{{before}} → {{after}} token',
  },
  memoryLine: {
    saved: 'Hafızaya kaydedildi: “{{content}}”',
    removed: 'Hafızadan kaldırıldı.',
    open: 'Hafızayı aç',
    undo: 'Geri al',
  },
  branch: { action: 'Buradan dal aç', from: 'Bu konuşma şundan dallandı:', fork: 'Arka planda dene (çatal)…' },
  diff: { title: 'Değişiklik', more: '{{count}} satır daha', stats: '{{added}} satır eklendi, {{removed}} satır silindi', show: 'Farkı göster' },
  side: {
    title: 'Yan sorular',
    hint: 'Konuşmayı değiştirmeden sorulur; yanıtlar kaydedilmez.',
    thinking: 'Yanıtlanıyor…',
    button: 'Yan soru',
  },
  slash: {
    title: 'Komutlar',
    commands: {
      compress: 'Konuşmayı özetleyip yer aç',
      clear: 'Yeni bir konuşma başlat',
      rewind: 'Önceki bir mesaja geri sar',
      export: 'Markdown ya da JSON olarak indir (/export json)',
      btw: 'Konuşmayı değiştirmeden yan soru sor',
      model: 'Bu konuşmanın modelini seç',
      memory: 'Hafıza sayfasını aç',
      help: 'Komutları göster',
      title: 'Konuşmanın adını değiştir',
      branch: 'Bu noktadan yeni bir dal aç',
      fork: 'Konuşmanın bir kopyası arka planda bir işi denesin; sonucu buraya gelir (/fork yönerge)',
      recap: 'Konuşmanın kısa özetini göster',
      context: 'Bağlam kullanımını göster',
      skill: 'Skill ekle: /skill add <GitHub adresi, bağlantı ya da library:ad>',
    },
    skill: 'skill',
    onlyInConversation: '{{command}} bir sohbetin içinde çalışır. Önce sohbeti başlatın.',
  },
  recap: {
    title: 'Özet',
    pending: 'Özet hazırlanıyor…',
    offer: 'Bu konuşmaya bir süre sonra döndünüz. Kısa bir özet ister misiniz?',
    action: 'Özetle',
  },
  export: { markdown: 'Markdown olarak indir', json: 'JSON olarak indir' },
  memory: {
    title: 'Hafıza',
    intro: 'Oppy yalnız sizin “bunu hatırla” dediklerinizi saklar. Belgeleriniz ve konuşmalarınız buraya kendiliğinden yazılmaz.',
    disabledByAdmin: 'Hafıza firmanızda kapalı. Firma yöneticiniz açabilir.',
    enabled: 'Hafıza açık',
    enabledHint: 'Kapalıyken kayıtlı bilgiler yanıtlara katılmaz ve yenisi kaydedilmez.',
    add: 'Hatırlanacak bir şey ekle',
    addPlaceholder: 'Ör. Raporlarım her zaman Türkçe ve tablo hâlinde olsun.',
    addButton: 'Ekle',
    stored: 'Kayıtlı bilgiler ({{count}})',
    empty: 'Henüz kayıtlı bilgi yok.',
    edit: 'Düzenle',
    fromConversation: 'Bir konuşmada istediniz',
    byYou: 'Buradan eklediniz',
    workspaceOnly: 'yalnız bir çalışma alanında',
    openConversation: 'konuşmayı aç',
  },
  // the library pages' one vocabulary for where a thing lives (D-118; SkillKutuphanesi, BaglantilarYeni, BelgelerYeni)
  library: {
    scopes: { all: 'Tümü', team: 'Ekip', organization: 'Herkes', user: 'Kişisel', workspace: 'Çalışma alanları', builtin: 'Standart', conversation: 'Sohbet ekleri' },
    tags: {
      team: 'Ekip',
      teamNamed: 'Ekip: {{name}}',
      organization: 'Herkes',
      user: 'Kişisel',
      workspace: 'Çalışma alanı',
      workspaceNamed: 'Alan: {{name}}',
      builtin: 'Standart',
      builtinFrom: 'Oppy ile gelir',
      conversation: 'Sohbet eki',
    },
    modes: { default_on: 'Varsayılan açık', required: 'Zorunlu' },
    appliesIn: 'Anahtarlar şurada geçerli:',
    everywhere: 'Her yerde',
    filter: 'Nerede olduğuna göre süz',
  },
  skills: {
    deleteTitle: "{{name}} skill'i silinsin mi?",
    deleteConfirm: "Skill'i sil",
    deleteShared: "Bu skill'i {{level}} düzeyinde kullanan herkes için kalkar.",
    scope: {
      label: 'Kimler kullanabilir?',
      short: 'Kapsam:',
      user: 'Kişisel',
      userHint: 'Yalnız siz',
      workspace: 'Bir çalışma alanı',
      workspaceHint: 'Alanın üyeleri',
      team: 'Bir ekip',
      teamHint: 'Ekibin üyeleri',
      organization: 'Herkes',
      organizationHint: 'Firmadaki herkes · yönetici onayıyla',
      organizationAdminHint: 'Firmadaki herkes',
    },
    license: {
      open: '{{name}} · açık kaynak',
      closed: 'Açık kaynak değil; firmada paylaşmadan önce kontrol edin',
      unknown: 'Lisans bilgisi yok',
    },
    preview: {
      label: 'Önizleme',
      files: 'Dosyaları gör',
      scripts_one: '{{count}} betik',
      scripts_other: '{{count}} betik',
      free: 'Sorulmadan çalışabilir',
      nothingFree: 'Hiçbir komut; hepsi sorulur',
      asked: 'Her seferinde sorulur',
      askedAll: 'Dosya yazma ve diğer bütün komutlar',
      notGiven: 'Verilmeyen izin',
      license: 'Lisans',
      // Phase 16c.3 (SkillGitHubBuyukDepo): what a skill that asks for nothing runs, the scripts asked by their language
      nothingAsked: 'Hiçbir şey; skill izin istemiyor',
      askedScripts: '{{scripts}}, dosya yazma ve diğer bütün komutlar',
      scriptLanguage_one: '{{count}} {{language}} betiği',
      scriptLanguage_other: '{{count}} {{language}} betiği',
      languages: { python: 'Python', shell: 'kabuk', javascript: 'JavaScript', typescript: 'TypeScript', powershell: 'PowerShell', ruby: 'Ruby' },
      more: 'Tamamını gör',
    },
    // Phase 16c.3: a GitHub repository (SkillGitHubBuyukDepo, SkillGitHubCoklu)
    github: {
      foundOne: '<b>Bu depoda 1 skill bulduk: {{name}}</b> ({{files}} dosya, {{size}}).',
      whole: 'Deponun tamamı {{files}} dosya ve {{size}}. Yalnız skill klasörünü indiriyoruz; geri kalanı kurulmaz.',
      wholeCut: 'Depo çok büyük; GitHub dosya listesinin bir kısmını verdi. Yalnız skill klasörünü indiriyoruz; geri kalanı kurulmaz.',
      found: '<b>Bu depoda {{count}} skill bulduk.</b> Kurmak istediklerinizi seçin.',
      onlyChosen: 'Yalnız seçtiğiniz skill klasörleri indirilir.',
      oneOver: 'Biri sınırı aşıyor; o kurulamaz, diğerleri kurulabilir.',
      someOver: '{{count}} tanesi sınırı aşıyor; onlar kurulamaz, diğerleri kurulabilir.',
      allOver: 'Hiçbiri sınırların içinde değil; skill’i ZIP olarak yükleyebilirsiniz.',
      which: 'Hangilerini kuralım?',
      selected: '{{count}} seçili · her birinin önizlemesi kurmadan önce açılır',
      meta: '{{files}} dosya · {{size}}',
      metaScripts: '{{files}} dosya · {{size}} · {{scripts}} betik',
      over: { bytes: 'Çok büyük: {{size}}', files: 'Çok fazla dosya: {{files}}', file: 'Çok büyük bir dosya: {{size}}' },
      overHint: {
        bytes: 'En çok {{limit}} · örnek dosyaları çıkarıp ZIP yükleyin',
        files: 'En çok {{limit}} dosya · örnek dosyaları çıkarıp ZIP yükleyin',
        file: 'Bir dosya en çok {{limit}} · o dosyayı çıkarıp ZIP yükleyin',
      },
      previewChosen_one: '{{count}} skill’i önizle',
      previewChosen_other: '{{count}} skill’i önizle',
      installAll: '{{count}} skill’i kur',
      installedMany: '{{names}} kuruldu.',
      installedManyHint: 'Asistan uygun işlerde bu skill’leri kendisi kullanır; /ad yazarak da çalıştırabilirsiniz.',
    },
    // Phase 16c.3: why an install failed (SkillGitHubHatalar, SkillGitHubCokBuyuk); a ZIP or SKILL.md link has plainer words
    sourceError: {
      details: 'Teknik ayrıntılar',
      copy: 'Kopyala',
      copied: 'Kopyalandı',
      upload: 'Dosyadan yükle',
      retry: 'Yeniden dene',
      wait: '{{minutes}} dk sonra dene',
      seconds: '{{value}} sn',
      underTenth: '{{value}} sn’den kısa',
      steps: { list: 'Depoyu listeleme', download: 'Skill klasörünü indirme', linkDownload: 'Dosyayı indirme', read: 'Skill’i okuma', install: 'Kurma' },
      labels: { step: 'Adım', address: 'Adres', answer: 'Cevap', connection: 'Bağlantı adımı', fallback: 'Klasik TLS', hosts: 'Gereken adresler', time: 'Süre', error: 'Hata numarası' },
      // Phase 16c.4: where a connection through egress stopped, and the classic TLS hello (D-397)
      connectStages: {
        proxy_dns: 'Vekilin adı (DNS)',
        proxy_tcp: 'Vekile bağlanma (TCP)',
        proxy_answer: 'Vekilin cevabı',
        tunnel_tls: 'Tünelde TLS el sıkışması',
        name_check: 'Oppy’nin adres denetimi (DNS)',
        body: 'Cevap gelirken koptu',
        unknown: 'Belli değil',
      },
      fallbacks: { tried: 'Denendi; o da sonuç vermedi', not_tried: 'Denenmedi (süre kalmadı)', not_needed: 'Gerekmedi (TLS’e gelinmeden durdu)' },
      answers: {
        tooLarge: 'Klasör {{size}} · {{files}} dosya · sınır {{limit}} ve {{limitFiles}} dosya',
        tooLargeFile: '{{path}} {{size}} · bir dosya için sınır {{limit}}',
        rateLimited: 'HTTP {{status}} · kalan istek {{remaining}} · sıfırlanma {{time}}',
        rateLimitedNoTime: 'HTTP {{status}} · istek sınırı',
        policy: 'Firma politikası engelledi, istek gönderilmedi',
        status: 'HTTP {{status}}',
        timeout: '{{seconds}} sn içinde bitmedi',
        network: 'Bağlantı kurulamadı ({{cause}})',
        networkProxy: 'Bağlantı kurulamadı (vekil {{status}})',
        noSkill: '{{files}} dosya listelendi, SKILL.md yok',
        noSkillCut: '{{files}} dosya listelendi (GitHub listenin bir kısmını verdi), SKILL.md yok',
        none: 'Cevap gelmedi',
      },
      github: {
        tooLarge: {
          title: 'Bu skill çok büyük.',
          bytes: 'Skill klasörü {{size}}, en çok {{limit}} kurulabiliyor. Örnek dosyaları çıkarıp ZIP olarak yükleyebilirsiniz.',
          files: 'Skill klasöründe {{files}} dosya var, en çok {{limit}} dosya kurulabiliyor. Örnek dosyaları çıkarıp ZIP olarak yükleyebilirsiniz.',
          file: '{{path}} dosyası {{size}}; bir dosya en çok {{limit}} olabilir. Bu dosyayı çıkarıp ZIP olarak yükleyebilirsiniz.',
        },
        rateLimited: {
          title: 'GitHub bizi bir süre bekletiyor.',
          when: 'Oppy, GitHub’ın bir saatte izin verdiği istek sayısına ulaştı. {{time}}{{from}} sonra yeniden deneyebilirsiniz (yaklaşık {{minutes}} dk).',
          passed: 'Oppy, GitHub’ın bir saatte izin verdiği istek sayısına ulaşmıştı. Şimdi yeniden deneyebilirsiniz.',
          unknown: 'Oppy, GitHub’ın bir saatte izin verdiği istek sayısına ulaştı. Biraz sonra yeniden deneyin.',
          zip: 'Beklemek istemiyorsanız skill’i ZIP olarak yükleyebilirsiniz.',
        },
        notAllowed: {
          title: 'GitHub’a bağlanma izni yok.',
          allow: 'Firmanızın izinli siteler listesinde GitHub adresleri yok, bu yüzden Oppy depoya ulaşamadı.',
          deny: 'Firmanızın engelli siteler listesinde GitHub adresleri var, bu yüzden Oppy depoya ulaşamadı.',
          admin: 'Yöneticiniz listeye ekleyebilir. Bu arada skill’i ZIP olarak yükleyebilirsiniz.',
          adminDeny: 'Yöneticiniz listeden çıkarabilir. Bu arada skill’i ZIP olarak yükleyebilirsiniz.',
        },
        notFound: {
          title: 'Bu depo bulunamadı.',
          body: 'Adres yanlış yazılmış olabilir ya da depo gizli. Oppy yalnız herkese açık depolardan kurabilir.',
          zip: 'Gizli bir depoysa skill klasörünü ZIP yapıp yükleyin.',
        },
        timeout: { title: 'GitHub zamanında cevap vermedi.', body: '{{seconds}} saniye bekledik, indirme bitmedi. GitHub yavaş olabilir. Birkaç dakika sonra yeniden deneyin.' },
        network: {
          title: 'GitHub’a ulaşılamadı.',
          body: 'Oppy sunucusu GitHub’a bağlanamadı. İnternet bağlantısında ya da GitHub’da geçici bir sorun olabilir.',
          admin: 'Sorun sürerse yöneticinize Teknik ayrıntılar’daki hata numarasını iletin.',
        },
        noSkill: {
          title: 'Bu adreste skill bulunamadı.',
          body: 'Depoda SKILL.md dosyası olan bir klasör yok. Skill’in klasörünü gösteren bağlantıyı yapıştırın, örneğin <code>github.com/sahip/depo/tree/main/skills/ad</code>.',
        },
        unavailable: { title: 'GitHub beklenmeyen bir cevap verdi.', body: 'Birkaç dakika sonra yeniden deneyin ya da skill’i ZIP olarak yükleyin.' },
      },
      link: {
        notAllowed: {
          title: 'Bu adrese bağlanma izni yok.',
          allow: 'Firmanızın izinli siteler listesinde {{host}} yok, bu yüzden Oppy dosyaya ulaşamadı.',
          deny: 'Firmanızın engelli siteler listesinde {{host}} var, bu yüzden Oppy dosyaya ulaşamadı.',
        },
        notFound: { title: 'Bu adreste dosya bulunamadı.', body: 'Adres yanlış yazılmış olabilir ya da dosya artık yok.' },
        timeout: { title: 'Kaynak zamanında cevap vermedi.', body: '{{seconds}} saniye bekledik, indirme bitmedi. Birkaç dakika sonra yeniden deneyin.' },
        network: { title: 'Kaynağa ulaşılamadı.', body: 'Oppy sunucusu bu adrese bağlanamadı. Bağlantıda ya da sitede geçici bir sorun olabilir.' },
        noSkill: { title: 'Bu adreste skill bulunamadı.', body: 'Dosyada ya da ZIP’te SKILL.md yok. SKILL.md dosyasının ya da skill klasörünün bağlantısını yapıştırın.' },
        unavailable: { title: 'Skill kaynağına ulaşılamadı.', body: 'Kaynak beklenmeyen bir cevap verdi. Birkaç dakika sonra yeniden deneyin ya da skill’i ZIP olarak yükleyin.' },
      },
    },
    introShort: "Oppy'ye tekrar eden işleri öğretin. Skill'ler hem {{chatProductIn}} hem {{codeProductIn}} çalışır.",
    levelsLabel: "Skill seviyeleri",
    searchLabel: 'Skill ara',
    addOwn: "Kendi skill'inizi ekleyin",
    addMenu: { url: "GitHub veya URL'den ekle", upload: 'Klasör veya ZIP yükle', conversation: 'Bir sohbetten kaydet', form: 'Form ile yeni skill yaz' },
    fromConversation: {
      search: 'Sohbetlerde ara',
      which: 'Hangi sohbet?',
      messages_one: '{{count}} mesaj',
      messages_other: '{{count}} mesaj',
      taken: 'Bu sohbetten çıkarılacaklar',
      reading: 'Sohbet okunuyor, adımlar çıkarılıyor…',
      noSteps: 'Numaralı adım bulunamadı; taslağı düzenleyicide tamamlayabilirsiniz.',
      skills: "Kullanılan skill'ler: {{names}}",
      noSkills: 'Bu sohbette skill kullanılmadı.',
      privacy: "Dosya içerikleri, rakamlar ve kişisel bilgiler skill'e konmaz; yalnız iş adımları alınır.",
      footer: 'Taslak, kaydetmeden önce düzenlemeniz için açılır.',
      create: 'Taslak oluştur',
      title: 'Bir sohbetten kaydet',
      hint: "Bir konuşma seçin; yaptıklarınızdan bir skill taslağı çıkar, kaydetmeden önce düzenleyebilirsiniz.",
      pick: 'Seç',
    },
    title: "Skill'ler",
    intro: "Skill'ler, sık yaptığınız işlerin tarifidir: adımlar, gerekirse betikler ve şablonlar. Asistan uygun olduğunda skill'i kendisi yükler; siz de mesajı /skill-adı ile başlatarak çalıştırabilirsiniz. Birden çok skill aynı işte birlikte kullanılabilir.",
    new: 'Yeni skill',
    place: 'Nerede',
    placeHint: 'Bir çalışma alanı seçerseniz açma/kapama yalnız orada geçerli olur.',
    everywhere: 'Her yerde (kişisel ayarım)',
    search: 'Ara',
    allLevels: 'Tümü',
    summaryNow: 'Şu an',
    summaryOn_one: '{{count}} skill açık',
    summaryOn_other: '{{count}} skill açık',
    summaryTipLabel: 'Ne kadar yer tutar?',
    summaryTip: "Açık skill'lerin adları ve kısa açıklamaları her istekte modele gönderilir; şu an yaklaşık {{tokens}} token.",
    summarySlash: "Kapalı bir skill'i sohbette “/” ile adını yazarak yine de çağırabilirsiniz.",
    sections: {
      teamTitle: 'Ekip · {{team}}',
      team: "{{team}} ekibinin skill'leri",
      teamAuto: "{{team}} ekibinin skill'leri; ekibe katılınca kendiliğinden açılır.",
      organization: 'Firmanızın herkes için eklediği',
      user: "Yalnız sizin skill'leriniz",
      workspace: 'Seçili çalışma alanında geçerli',
      builtin: 'Oppy ile hazır gelir; her şirkette aynıdır',
      otherTeams: 'Diğer ekipler (yönetim için)',
      otherTeamsHint: 'Üyesi olmadığınız ekipler',
      otherTeamsNote: "Bu ekiplerin üyesi değilsiniz: skill'lerini yönetmek için görürsünüz ve sizde kapalıdırlar; isterseniz kendiniz için açabilirsiniz.",
      count_one: '{{count}} skill',
      count_other: '{{count}} skill',
    },
    analyticsLink: "Skill'lerin kullanımı: Yönetim → Skill analitiği",
    standard: {
      'web-research': "Web'de araştırır, kaynakları okur ve kaynak göstererek özetler.",
      'data-report': 'Excel ya da CSV verisinden grafikli rapor hazırlar.',
      'skill-writer': 'Sohbetten yeni bir skill yazmanıza yardım eder.',
      'code-review': 'Kodu inceler, hata ve iyileştirme önerir.',
    },
    empty: 'Burada skill yok.',
    details: 'Ayrıntılar',
    on: 'Açık',
    off: 'Kapalı',
    toggle: "{{name}} skill'ini aç ya da kapat",
    version: 'sürüm {{version}}',
    mayRun: 'Komut çalıştırabilir',
    modelOnly: 'Yalnız asistan kullanır',
    shadowed: 'Aynı adlı bir skill ({{level}}) bunun yerine kullanılıyor.',
    hides: "Aynı adlı şu skill'lerin yerine kullanılıyor: {{levels}}.",
    cost: 'Listede ~{{listing}} token; yüklenince ~{{body}} token.',
    whenToUse: 'Ne zaman: {{text}}',
    slashHint: 'Çalıştırmak için: /{{name}} {{hint}}',
    source: 'Kaynak: {{url}}',
    raw: 'SKILL.md dosyasını göster',
    versions: 'Sürümler',
    inUse: 'Kullanılan',
    rollback: 'Bu sürüme dön',
    submit: 'İncelemeye gönder',
    approve: 'Onayla',
    reject: 'Reddet',
    reviewNote: 'inceleme notu: {{note}}',
    reviewNotePlaceholder: 'İnceleme notu (isteğe bağlı)',
    edit: 'Düzenle',
    propose: 'Değişiklik öner',
    updateFromSource: 'Kaynaktan güncelle',
    download: 'İndir (ZIP)',
    confirmDelete: '{{name}} ve bütün sürümleri silinir. Bu geri alınamaz.',
    hooksIgnored: "Bu skill'deki kancalar ({{hooks}}) çalıştırılmaz.",
    usageOwn: 'Son 30 günde {{loads}} kez · {{users}} kişi',
    levels: { builtin: 'Standart', organization: 'Herkes', team: 'Ekip', user: 'Kişisel', workspace: 'Çalışma alanları' },
    status: { draft: 'Taslak', in_review: 'İncelemede', published: 'Yayında', rejected: 'Reddedildi' },
    catalog: {
      label: 'Üyeler için',
      hint: "Firma ve ekip skill'leri üyelere nasıl sunulsun?",
      available: 'Listede, isteyen açar',
      default_on: 'Herkeste açık, isteyen kapatır',
      required: 'Herkeste açık, kapatılamaz',
      requiredHint: "Firmanız bu skill'i zorunlu tuttu.",
    },
    permissions: {
      title: 'Ne çalıştırabilir',
      granted: 'Çalıştığı tur boyunca sorulmadan çalışabilecekler:',
      ownScripts: "skill'in kendi betikleri",
      scope: 'Bu izinler yalnız skill yüklendiği turda geçerlidir; skill kapatılınca ya da değişince hemen kalkar. Diğer komutlar her zaman sorulur.',
      none: 'Bu skill sorulmadan hiçbir komut çalıştırmaz; komutları gerekirse size sorulur.',
      ignored: 'Skill istedi ama verilmeyenler:',
    },
    ignoredReasons: {
      tool_level_shell: "bütün kabuk komutları verilmez; yalnız skill'in kendi betikleri",
      tool_level_write: 'dosya yazma her zaman sorulur',
      too_broad: 'çok geniş (bütün bir program ya da her kod)',
      unknown_tool: 'tanınmayan araç',
      unsupported_syntax: 'desen anlaşılamadı',
    },
    fileKinds: { skill: 'talimat', script: 'betik', template: 'şablon', reference: 'başvuru', other: 'diğer' },
    warnings: {
      name_normalized: 'Ad, kurallara uygun hale getirildi.',
      description_long: 'Açıklama çok uzun; kısaltılırsa doğru seçilmesi kolaylaşır.',
      body_large: 'Talimatlar uzun; parça parça yüklenir.',
      hooks_ignored: 'Kancalar çalıştırılmaz.',
      trust_ignored: 'Güven ayarı yok sayıldı; yalnız firma yöneticisi belirler.',
      no_scripts: 'İzinler betik çalıştırmayı istiyor ama pakette betik yok.',
      frontmatter_lenient: 'Ön bilgi tam YAML değil; satır satır okundu. İki nokta içeren değerleri tırnağa alın.',
    },
    add: {
      linkLabel: 'GitHub deposu, ZIP ya da SKILL.md bağlantısı',
      examples: 'Örnekler:',
      sources: 'Kaynak',
      subtitle: 'Kurmadan önce içeriğini ve sorulmadan neleri çalıştırabileceğini gösteririz.',
      subtitleConversation: "Bir sohbette yaptığınız işi tekrar kullanılabilir bir skill'e çeviririz.",
      tabs: { link: 'Bağlantıdan', file: 'Dosyadan', write: 'Kendin yaz', conversation: 'Bir sohbetten' },
      dropTitle: "ZIP, klasör ya da SKILL.md'yi buraya sürükleyin",
      pickFile: 'Dosya seç',
      pickFolder: 'Klasör seç',
      dropHint: '.zip ya da .md · en çok 20 MB · içinde birden çok skill varsa hepsi listelenir',
      read: 'Okundu',
      removeFile: 'Dosyayı kaldır',
      dragHint: 'ZIP ya da klasörü bu pencereye sürükleyebilirsiniz',
      writeTitle: "Kendi skill'inizi yazın",
      writeHint: 'Adını, ne yaptığını ve adımlarını bir formda ya da doğrudan SKILL.md olarak yazarsınız; kaydetmeden önce deneyebilirsiniz.',
      writeStart: 'Yazmaya başla',
      open: "Skill'i aç",
      button: 'Skill ekle',
      title: 'Skill ekle',
      hint: 'GitHub deposu (sahip/depo ya da bağlantı), bir ZIP ya da SKILL.md bağlantısı, ya da firma kütüphanesinden library:ad. Kurmadan önce içeriğini ve ne çalıştırabileceğini görürsünüz.',
      source: 'Kaynak',
      placeholder: 'ör. anthropics/skills/skills/xlsx ya da library:aylik-rapor',
      preview: 'Önizle',
      orUpload: 'ya da yükleyin:',
      uploadFile: 'ZIP ya da SKILL.md',
      uploadFolder: 'Klasör',
      commit: 'Sürüm (commit): {{commit}}',
      chooseFolder: 'Bu kaynakta birden çok skill var',
      chooseFolderHint: 'Bir klasör seçin…',
      files: '{{count}} dosya',
      existing: "Bu adda bir skill'iniz var ({{level}}, sürüm {{version}}). Aynı yere kurarsanız yeni sürüm olur.",
      install: 'Kur',
      update: 'Yeni sürüm olarak kur',
      installed: '{{name}} kuruldu.',
      installedHint: "Asistan uygun işlerde {{name}} skill'ini kendisi kullanır; /{{name}} yazarak da çalıştırabilirsiniz.",
    },
    target: {
      label: 'Nereye',
      propose: 'Herkes için öner',
      workspace: 'Çalışma alanı',
      team: 'Ekip',
    },
    editor: {
      mode: 'Düzenleme biçimi',
      side: 'Önizleme ve deneme',
      argumentHintHint: '"/" menüsünde adın yanında görünür.',
      whenToUseHint: 'Örnek istekler yazın; asistan buna da bakarak seçer.',
      nameHintNamed: 'Küçük harf ve tire. Sohbette /{{name}} olarak da çağrılır.',
      nameOk: 'Uygun',
      namePlaceholder: 'skill-adi',
      descriptionPlaceholder: 'Ne yaptığı burada görünür',
      addFiles: 'Dosya ekle',
      addFilesHint: 'Betik, şablon ya da başvuru dosyası. Betik eklerseniz sorulmadan çalışabilecek komutu ayrıca seçersiniz.',
      grantScripts: 'Eklenen betikler sorulmadan çalışabilsin',
      filesKept: "Bu skill'in {{count}} dosyası olduğu gibi kalır.",
      menuPreview: '"/" menüsünde böyle görünür',
      checksTitle: 'Kontroller',
      checks: {
        frontmatter: 'SKILL.md, --- satırları arasında ön bilgiyle başlamalı',
        noName: 'Bir ad yazın',
        badName: 'Ad küçük harf, rakam ve tireden oluşmalı',
        taken: 'Bu adda bir skill zaten var ({{level}}); kaydederseniz yanında ikinci bir skill olur',
        name: 'Ad uygun ve benzersiz',
        noDescription: 'Ne yaptığını yazın',
        shortDescription: 'Açıklamayı ne ürettiğini söyleyecek kadar somut yazın',
        longDescription: 'Açıklama {{max}} karakteri geçmesin',
        description: 'Açıklama somut, ne ürettiğini söylüyor',
        noWhen: '"Ne zaman kullanılır" alanına örnek istekler eklemek seçimi kolaylaştırır',
        when: 'Ne zaman kullanılacağı yazılı',
        noBody: 'Talimat yazın',
        noSteps: 'Talimatları numaralı adımlarla yazmak takip etmeyi kolaylaştırır',
        steps: 'Talimatlar adım adım',
        longBody: 'Talimatlar uzun; parça parça yüklenir',
      },
      try: 'Kaydetmeden dene',
      tryLabel: 'Deneme isteği',
      tryPlaceholder: "ör. Ahmet'e toplantıyı cumaya ertelediğimizi yaz",
      tryRun: 'Dene',
      tryNote: 'Kaydedilmez; deneme turu kullanım bütçenizden düşer.',
      tryChosen: "Skill kendiliğinden seçildi · {{seconds}} sn'de hazırlandı",
      tryNotChosen: 'Skill kendiliğinden seçilmedi; /{{name}} ile çalıştırılmış gibi denendi · {{seconds}} sn',
      tryOther: "Asistan önce {{other}} skill'ini seçti; taslak, adıyla çalıştırılmış gibi denendi · {{seconds}} sn",
      newTitle: 'Yeni skill',
      editTitle: "{{name}} skill'ini düzenle",
      fromConversation: 'Bu taslak konuşmadan çıkarıldı. Gözden geçirip kaydedin.',
      form: 'Form',
      name: 'Ad',
      nameHint: 'Küçük harf ve tire, ör. aylik-gelir-raporu. /ad olarak da çalıştırılır.',
      description: 'Ne yapar',
      descriptionHint: "Asistan skill'i bu cümleye bakarak seçer. Ne ürettiğini somut yazın.",
      whenToUse: 'Ne zaman kullanılır',
      argumentHint: 'Argüman ipucu',
      body: 'Talimatlar',
      bodyHint: 'Yaklaşık {{tokens}} token. Uzun ayrıntıları ayrı dosyalara koyun.',
      allowedTools: 'Sorulmadan çalışabilecek komutlar (isteğe bağlı)',
      allowedToolsHint: "Her satıra bir komut deseni. Yalnız belirli komutlar ve skill'in kendi betikleri verilir.",
      rawHint: "Ön bilgi (---) ve talimatlar. Form ve bu alan aynı skill'i düzenler.",
      changeNote: 'Değişiklik notu',
      saveVersion: 'Yeni sürüm olarak kaydet',
    },
    review: { title: 'İnceleme bekleyen öneriler ({{count}})' },
  },
  connectors: {
    deleteTitle: '{{name}} bağlantısı silinsin mi?',
    title: 'Bağlantılar',
    intro: 'Bağlantılar (MCP) asistanın başka hizmetlerdeki araçları kullanmasını sağlar: takvim, belge depoları, veritabanları, şirket içi sistemler. Anahtarlarınız ve oturumlarınız sunucuda şifreli saklanır; tarayıcıya geri gönderilmez.',
    placeHint: 'Bir çalışma alanı seçerseniz o alanın bağlantıları ve oradaki açma/kapama görünür.',
    one: 'bağlantı',
    tool: 'bağlantı aracı',
    // BaglantilarYeni (D-118)
    introShort: "Oppy'nin başka sistemlerdeki araçları kullanmasını sağlar: e-posta ve takvim, belge depoları, veritabanları, şirket içi sistemler.",
    introPersonal: "Oppy'nin başka hizmetlerdeki araçları kullanmasını sağlar.",
    safe: 'Anahtarlarınız ve oturumlarınız sunucuda şifreli saklanır; tarayıcıya geri gönderilmez.',
    search: 'Bağlantı ara',
    summaryNow: 'Şu an',
    summaryOn_one: '{{count}} bağlantı açık',
    summaryOn_other: '{{count}} bağlantı açık',
    summaryTools_one: 'asistan {{tools}} araç kullanabilir',
    summaryTools_other: 'asistan {{tools}} araç kullanabilir',
    sections: {
      teamTitle: 'Ekip · {{team}}',
      team: 'Ekibinizin bağlantıları',
      organization: 'Şirketin herkes için eklediği bağlantılar',
      user: 'Yalnız sizin bağlantılarınız',
      workspace: 'Çalışma alanlarına eklenen bağlantılar',
      count_one: '{{count}} bağlantı',
      count_other: '{{count}} bağlantı',
    },
    emptyAll: 'Henüz bağlantı yok.',
    emptyScope: {
      organization: 'Firmanız henüz herkes için bağlantı eklemedi.',
      team: 'Ekiplerinizin bağlantısı yok.',
      user: 'Henüz kendi bağlantınız yok.',
      workspace: 'Çalışma alanlarında bağlantı yok.',
    },
    state: { ready: 'Hazır', account: 'Hesabınızı bağlayın', down: 'Ulaşılamıyor' },
    location: { local: 'Kod alanında', internal: 'Şirket içi', remote: 'Uzak sunucu' },
    moreTools_one: '+{{count}} araç',
    moreTools_other: '+{{count}} araç',
    toolsAfterSignIn: 'Araçlar, hesabınızı bağladıktan sonra görünür.',
    connectAccount: 'Hesabımı bağla',
    eachOwnAccount: 'Her kişi kendi hesabıyla bağlanır',
    details: 'Ayrıntılar',
    actions: '{{name}} için işlemler',
    scopes: { tenant: 'Herkes', user: 'Kişisel', workspace: 'Çalışma alanları' },
    trusted: 'Firmaca güvenilir',
    required: 'Zorunlu',
    blocked: {
      disabled: 'Kapalı',
      plan_required: 'Pro’da açılır',
      disabled_by_admin: 'Firmanız kişisel bağlantıları kapattı',
      untrusted_workspace: 'Güvenilmeyen çalışma alanında çalışmaz',
      local_in_chat: 'Yerel bağlantılar yalnız kod çalışma alanında çalışır',
      not_approved: 'Ortak yapılandırma onayınızı bekliyor',
      needs_auth: 'Oturum açmanız gerekiyor',
      needs_credentials: 'Anahtar girmeniz gerekiyor',
      not_discovered: 'Araçları henüz okunmadı',
      error: 'Şu an ulaşılamıyor',
    },
    tools: 'Araçlar ({{count}})',
    noTools: 'Henüz araç listesi yok. "Yenile" ile yeniden okuyun.',
    readOnly: 'yalnız okur',
    policyFor: '{{tool}} için izin',
    policy: { ask: 'Her seferinde sor', allow: 'Sormadan çalıştır', deny: 'Kapalı' },
    instructions: 'Hizmetin notları',
    lastError: 'Son bağlantı denemesi başarısız oldu.',
    lastUsed: 'Son kullanım: {{date}}',
    toggle: '{{name}} bağlantısını aç ya da kapat',
    signIn: 'Oturum aç',
    signInAgain: 'Yeniden oturum aç',
    enterKeys: 'Anahtar gir',
    forgetKeys: 'Anahtarımı sil',
    approve: 'Yapılandırmayı onayla',
    refresh: 'Yenile',
    restart: 'Yeniden başlat',
    trust: 'Güvenilir işaretle',
    untrust: 'Güveni kaldır',
    trustHint: 'Güvenilir bağlantıda yalnız hizmetin salt okunur dediği araçlar sorulmadan çalışır. Bir şey değiştiren ya da gönderen araçlar yine sorulur; sormadan çalışması için aracın iznini "Sormadan çalıştır" yapın.',
    confirmDelete: 'Oturumları ve saklanan anahtarları da silinir.',
    userServersOff: 'Firmanız kendi bağlantınızı eklemeyi kapattı; Herkes bölümündekileri kullanabilirsiniz.',
    credentials: { shared: 'Bu değerler firmadaki herkes için kullanılır.', personal: 'Bu değerler yalnız sizin bağlantınız için saklanır.' },
    oauth: { connected: 'Bağlantı kuruldu.', failed: 'Oturum açılamadı.' },
    add: {
      button: 'Bağlantı ekle',
      title: 'Bağlantı ekle',
      hint: 'Önce "Bağlantıyı dene" ile araçlarını görün, sonra kaydedin.',
      where: 'Kimin için',
      workspace: 'Çalışma alanı',
      kind: 'Tür',
      http: 'Uzak sunucu (HTTP)',
      sse: 'Uzak sunucu (SSE, eski)',
      stdio: 'Yerel komut (kod alanının sandbox’ında)',
      name: 'Kısa ad',
      nameHint: 'Küçük harf, rakam, tire; araç adlarında görünür.',
      displayName: 'Görünen ad',
      description: 'Açıklama',
      stdioHint: 'Yerel bağlantı yalnız sizin kod çalışma alanınızın sandbox’ında, sizin yetkinizle çalışır.',
      command: 'Komut',
      args: 'Argümanlar',
      argsHint: 'Her satıra bir argüman.',
      url: 'Adres',
      auth: 'Kimlik doğrulama',
      authNone: 'Yok',
      authOauth: 'Oturum açarak (OAuth)',
      authHeaders: 'API anahtarı ya da başlık',
      oauthScope: 'Kapsam (isteğe bağlı)',
      oauthHint: 'Çoğu hizmette boş bırakılabilir.',
      oauthAfterSave: 'Kaydettikten sonra "Oturum aç" ile bağlanın.',
      env: 'Ortam değişkenleri',
      headers: 'Başlıklar',
      pairName: 'Ad',
      pairValue: 'Değer',
      addPair: 'Ekle',
      secretsHint: 'Değerler sunucuda şifreli saklanır ve bir daha gösterilmez.',
      catalog: 'Üyeler için',
      shared: 'Herkes bu anahtarı kullansın',
      import: 'Başka bir uygulamanın JSON ayarını içe aktar',
      importButton: 'İçe aktar',
      test: 'Bağlantıyı dene',
      testOk: 'Bağlantı çalışıyor: {{count}} araç bulundu.',
      testFailed: 'Bağlantı kurulamadı.',
    },
  },
  teams: {
    deleteTitle: '{{name}} ekibi silinsin mi?',
    title: 'Ekipler',
    intro: "Ekipler, skill kütüphanesinde kendi skill'lerini paylaşır. Ekip lideri ekibin skill'lerini yönetir.",
    empty: 'Henüz ekip yok.',
    count: '{{count}} üye',
    lead: 'Lider',
    member: 'Üye',
    role: 'Rol',
    remove: 'Çıkar',
    addMember: 'Ekibe ekle',
    pickMember: 'Bir üye seçin…',
    new: 'Ekip oluştur',
    newPlaceholder: 'Ekip adı, ör. Finans',
    confirmDelete: "Ekibin skill'leri de silinir.",
  },
  webPolicy: {
    title: 'Web ve entegrasyonlar',
    intro: 'Asistanın web’de arama ve sayfa okuma kuralları; dış kaynaklardan skill kurma ve kişisel bağlantılar.',
    provider: 'Web araması',
    platformMissing: 'Platform araması bu kurulumda tanımlı değil; başka bir sağlayıcı seçin.',
    providers: { platform: 'Platformun araması', searxng: 'Kendi SearXNG sunucumuz', off: 'Kapalı' },
    searxngUrl: 'SearXNG adresi',
    apiKey: 'API anahtarı',
    keySaved: 'Anahtar kayıtlı (gösterilmez).',
    keyMissing: 'Anahtar girilmedi.',
    saveKey: 'Anahtarı kaydet',
    removeKey: 'Anahtarı sil',
    fetch: 'Web sayfası okuma',
    fetchHint: 'Asistan, kullanıcının verdiği ya da aramanın getirdiği sayfaları okuyabilir.',
    allow: 'Yalnız bu siteler',
    allowHint: 'Boşsa her site; doluysa yalnız bunlar (her satıra bir alan adı).',
    deny: 'Engelli siteler',
    denyHint: 'Her satıra bir alan adı.',
    skillSources: 'Dış kaynaklardan skill kurma',
    skillSourcesHint: "Kapalıyken yalnız Herkes bölümündeki skill'ler kurulur.",
    userServers: 'Kişisel bağlantılar',
    userServersHint: 'Kapalıyken üyeler yalnız Herkes bölümündeki bağlantıları kullanır.',
  },
  skillChip: {
    skill: 'Skill',
    loaded: '{{name}} yüklendi',
    runByYou: '/{{name}} çalıştı',
    reused: '{{name}} zaten yüklüydü; yeniden eklenmedi',
    part: 'parça {{part}}/{{parts}}',
    grants: 'bu tur sorulmadan: {{list}}',
    grantedBy: "{{name}} skill'inin izniyle",
    added: 'Eklendi: {{list}}',
    removed: 'Kaldırıldı ya da kapatıldı: {{list}}',
  },
  sources: { title: 'Kaynaklar ({{count}})' },
  documents: {
    deleteTitle: '{{name}} silinsin mi?',
    title: 'Belgeler',
    intro: 'Belgeleriniz burada durur. Sohbetlerde aranır; yanıtlar sayfa, satır ya da slayt numarasıyla kaynak gösterir.',
    button: 'Belgeler ({{count}})',
    inConversation: 'Bu konuşmanın belgeleri',
    noneInConversation: 'Bu konuşmada henüz belge yok. Dosya ekleyin ya da kütüphaneden seçin.',
    removeFromConversation: '{{name}} bu konuşmadan çıkarılsın',
    addFromLibrary: 'Kütüphaneden belge ekle',
    libraryEmpty: 'Kütüphanede eklenecek belge yok',
    openLibrary: 'Belge kütüphanesini aç',
    upload: 'Belge yükle',
    uploadTo: 'Nereye',
    accepted: 'PDF, Word, Excel, PowerPoint, metin, Markdown, CSV ve görseller.',
    uploaded_one: '{{count}} belge yüklendi; okunuyor.',
    uploaded_other: '{{count}} belge yüklendi; okunuyor.',
    filter: 'Belgeleri süz',
    all: 'Tümü',
    search: 'Belge adında ara',
    noMatch: 'Bu adla belge yok.',
    empty: 'Henüz belge yok. Yüklediğiniz belgeler burada görünür.',
    share: 'Paylaş',
    shareWith: 'Kiminle paylaşılsın?',
    keep: 'Kütüphanede sakla',
    workspaceNamed: 'Çalışma alanı: {{name}}',
    actions: '{{name}} için işlemler',
    confirmDelete: 'Metni, dizini ve önizlemeleri de silinir; bu belgeye dayanan kaynaklar artık açılmaz.',
    scope: { user: 'Kişisel', workspace: 'Çalışma alanları', organization: 'Herkes', conversation: 'Sohbet ekleri' },
    // BelgelerYeni (D-118)
    uploadTarget: 'Nereye yüklensin?',
    uploadPlace: 'yüklenecek yer',
    dropHere: 'Dosyaları buraya bırakın',
    dropOr: 'ya da',
    choose: 'bilgisayardan seçin',
    acceptedShort: 'PDF, Word, Excel, PowerPoint, metin, Markdown, CSV, görsel · en çok {{mb}} MB',
    columns: { name: 'Belge', where: 'Nerede', source: 'Kaynak', size: 'Boyut', chat: 'Sohbette', state: 'Durum', added: 'Eklenme' },
    sort: { label: 'Sıralama', option: 'Sıra: {{order}}', date: 'Son eklenen', dateAsc: 'İlk eklenen', name: 'Ada göre', size: 'Boyuta göre' },
    // Microsoft 365 part 3: copies of OneDrive, SharePoint and Outlook files (MsBelgelerKaynak)
    source: {
      filter: 'Kaynak',
      all: 'Tümü',
      upload: 'Bilgisayardan',
      onedrive: 'OneDrive',
      sharepoint: 'SharePoint',
      sharepointSite: 'SharePoint · {{site}}',
      outlook: 'Outlook eki',
      outlookFrom: 'Outlook · {{sender}}',
      box: 'Kaynak',
      fromMicrosoft: "Microsoft 365'ten…",
    },
    sourceState: {
      importing: 'İçe aktarılıyor',
      refetching: 'Yeniden alınıyor…',
      importFailed: 'Alınamadı',
      refetchFailed: 'Yeniden alınamadı',
      changed: 'Kaynakta değişti',
      gone: 'Kaynakta bulunamadı',
      unchanged: 'Kaynakta değişiklik yok',
      updated: 'Yeniden alındı',
    },
    aside: {
      fetchedBy: 'Getiren',
      atSource: 'Kaynaktaki',
      where: 'Nerede',
      size: 'Boyut',
      fetched: '{{name}} · {{when}}',
      changedBy: '{{when}} {{by}} değiştirdi',
      changed: '{{when}} değiştirildi',
      place: {
        user: 'Kişisel · yalnız siz görürsünüz',
        team: 'Ekip: {{name}} · ekipteki herkes görür',
        workspace: 'Çalışma alanı: {{name}} · çalışma alanındaki herkes görür',
        organization: 'Herkes · firmadaki herkes görür',
        conversation: 'Sohbet eki · yalnız o sohbette kullanılır',
      },
      whole: 'tamamı okunur',
      large: 'büyük · ilgili bölümlerden yanıtlanır',
      copy: {
        sharepoint: "Bu bir kopyadır. SharePoint'teki dosya değişirse burada kendiliğinden değişmez; “Yeniden al” ile güncelleyin.",
        onedrive: "Bu bir kopyadır. OneDrive'daki dosya değişirse burada kendiliğinden değişmez; “Yeniden al” ile güncelleyin.",
        outlook: 'Bu bir kopyadır. Mail ekleri değişmez.',
      },
      refetchFailed: 'Yeniden alınamadı: {{reason}}. Eski kopya duruyor.',
      unchanged: 'Kaynakta değişiklik yok; kopya aynı kaldı.',
      updated: 'Yeniden alındı; kopya güncel.',
    },
    refetch: {
      action: 'Yeniden al',
      busy: 'Yeniden alınıyor…',
      connect: 'Yeniden almak için Microsoft hesabınızı bağlayın',
      scopes: 'Yeni izin gerekiyor',
      reconnect: 'yeniden bağlan',
      off: "Microsoft 365'ten belge getirme şu an kapalı; kopya Belgeler'de duruyor.",
      switchedOff: "Microsoft 365'i Bağlantılar'da kapattınız; yeniden almak için açın.",
      gone: "Dosya Microsoft'ta bulunamadı; silinmiş ya da taşınmış olabilir.",
      forbidden: "Bu dosyayı Microsoft'ta göremiyorsunuz; yeniden alamazsınız.",
      outlook: 'Mail ekleri değişmez',
    },
    // a time in a sentence, with its Turkish ending ("28 Eki'de", "bugün 10:12'de")
    when: { today: "bugün {{time}}'{{suffix}}", yesterday: "dün {{time}}'{{suffix}}", date: "{{date}}'{{suffix}}" },
    sections: {
      teamTitle: 'Ekip · {{team}}',
      organization: 'herkes için eklenen',
      user: 'yalnız sizin',
      workspace: 'çalışma alanlarındaki',
      conversation: 'sohbetlere eklenen dosyalar',
      count_one: '{{count}} belge',
      count_other: '{{count}} belge',
    },
    inChat: '“{{title}}” sohbetinde',
    inSomeChat: 'Bir sohbetin eki',
    chat: {
      whole: 'Tamamı okunur',
      large: 'Büyük · ilgili bölümlerden yanıtlanır',
      wholeTokens: '~{{tokens}} token',
      largeTokens: '~{{tokens}} token · soru başına ~{{question}}',
      pending: 'Hazır olunca belli olur',
    },
    why: 'Neden?',
    retry: 'Yeniden dene',
    rename: 'Yeniden adlandır',
    explain: '“Tamamı okunur”: belge soruyla birlikte bütünüyle modele verilir. “Büyük”: yalnız soruyla ilgili bölümler bulunur ve kaynak gösterilir; bütün belgeyi özetlemek uzun sürer.',
    size: {
      pages_one: '{{count}} sayfa',
      pages_other: '{{count}} sayfa',
      sheets_one: '{{count}} sayfa',
      sheets_other: '{{count}} sayfa',
      slides_one: '{{count}} slayt',
      slides_other: '{{count}} slayt',
      tokens: '~{{tokens}} token',
    },
    state: {
      queued: 'Sırada',
      ready: 'Hazır',
      failed: 'Okunamadı: {{reason}}',
      failedShort: 'Okunamadı',
      processing: 'İşleniyor',
      processingAt: 'İşleniyor %{{percent}}',
      importFailed: 'Alınamadı: {{reason}}',
    },
    stage: { importing: 'İçe aktarılıyor', extracting: 'Metin okunuyor', converting: 'Dönüştürülüyor', ocr: 'Metin tanınıyor', indexing: 'Dizinleniyor' },
    errors: {
      encrypted: 'parola korumalı',
      invalid_document: 'dosya bozuk ya da okunamıyor',
      archive_too_large: 'açıldığında çok büyük',
      timeout: 'okuma çok uzun sürdü',
      conversion_failed: 'dönüştürülemedi',
      ocr_failed: 'metin tanınamadı',
      unsupported: 'bu tür okunamıyor',
      empty: 'dosya boş',
      processing_failed: 'beklenmeyen bir sorun oldu',
      // a Microsoft 365 copy that could not be made (DOCUMENT_SOURCE_ERRORS)
      source_forbidden: "bu dosyayı Microsoft'ta görme izniniz yok",
      source_not_found: "dosya Microsoft'ta bulunamadı; silinmiş ya da taşınmış olabilir",
      source_too_large: 'dosya boyut sınırını aşıyor',
      source_unsupported: 'bu dosya türü okunamıyor',
      source_malware: 'Microsoft bu dosyada zararlı yazılım buldu',
      source_empty: 'dosya boş',
      source_off: "Microsoft 365'ten belge getirme kapalı",
      source_not_connected: 'Microsoft hesabı bağlı değil; bağlayıp yeniden deneyin',
      microsoft_scopes_required: 'yeni izin gerekiyor; Microsoft hesabınıza yeniden bağlanın',
      source_unavailable: "Microsoft'a ulaşılamadı; biraz sonra yeniden deneyin",
    },
    estimate: {
      fits: 'Yaklaşık {{tokens}} token; bağlama sığar. Her soru yaklaşık {{question}} token ekler.',
      large: 'Yaklaşık {{tokens}} token; bağlamdan büyük. Sorular ilgili bölümlerden yanıtlanır (her soru yaklaşık {{question}} token); bütün belgeyi özetlemek uzun sürer.',
    },
    quality: { good: 'iyi', fair: 'orta', low: 'düşük' },
    notes: {
      ocr_used: '{{pages}} sayfada metin görüntüden tanındı (kalite: {{quality}}).',
      ocr_low_quality: '{{pages}} sayfada metin görüntüden tanındı ama kalite düşük: rakamlar ve adlar yanlış okunmuş olabilir.',
      truncated: 'Yalnız ilk {{pages}} sayfa okundu (toplam {{total}}).',
      no_text: 'Belgede okunabilir metin bulunamadı.',
      image_no_text: 'Görselde okunabilir yazı yok; görselin kendisini anlayan model henüz yok.',
      sheet_truncated: '{{sheet}} sayfasının yalnız bir bölümü okundu.',
      keyword_only: 'Anlam araması şimdilik yok; belge kelime eşleşmesiyle aranıyor.',
    },
    preview: {
      title: 'Önizleme',
      previous: 'Önceki sayfa',
      next: 'Sonraki sayfa',
      pageOf: 'Sayfa {{page}} / {{total}}',
      slideOf: 'Slayt {{page}} / {{total}}',
      openFull: 'Belgeyi tam ekranda aç',
      quote: 'Kaynaktaki ifade',
      recognized: 'Tanınan metin',
      empty: 'Gösterilecek bir şey yok.',
      truncated: 'Yalnız ilk satırlar gösteriliyor.',
      notYet: 'Önizleme belge okununca hazır olur.',
    },
  },
  citations: {
    title: 'Kaynaklar',
    page: 's. {{number}}',
    slide: 'slayt {{number}}',
    row: 'satır {{number}}',
    sheet: 'sayfa',
    section: 'bölüm {{number}}',
    gone: 'Bu kaynağın belgesi artık yok (silinmiş ya da erişiminiz kalkmış). Alıntı:',
    searched: '{{count}} belgede arandı · {{sources}} kaynak',
    searchedNone: '{{count}} belgede arandı',
  },
  convTools: {
    button: "Skill'ler ve bağlantılar",
    skills: "Bu konuşmada skill'ler",
    hint: 'Buradaki açma/kapama yalnız bu konuşma için geçerlidir.',
    saveAsSkill: 'Bunu skill olarak kaydet',
    manageSkills: "Skill'leri yönet",
    connectors: 'Bu konuşmada bağlantılar',
    noConnectors: 'Kullanılabilir bağlantı yok.',
    manageConnectors: 'Bağlantıları yönet',
    addSkill: 'Skill ekle',
  },
  admin: {
    title: 'Yönetim',
    nav: 'Yönetim menüsü',
    onlyAdmins: 'Bu sayfayı yalnız firma yöneticileri açabilir.',
    onlySuperAdmins: 'Bu sayfayı yalnız süper yöneticiler açabilir.',
    sections: {
      access: 'Erişim ve yetkiler',
      models: 'Modeller',
      integrations: 'Entegrasyonlar',
      usage: 'Kullanım ve limitler',
      policies: 'Onay politikaları',
      skills: 'Skill analitiği',
      audit: 'Denetim kaydı',
      identity: 'Kimlik doğrulama',
      license: 'Lisans',
    },
    csv: 'CSV',
    csvExport: 'Dışa aktar (CSV)',
    table: {
      selectPage: 'Bu sayfadaki satırları seç',
      actions: 'İşlemler',
      empty: 'Gösterilecek satır yok.',
      range: '{{from}}–{{to}} / {{total}} {{noun}}',
      perPage: 'Sayfa başına',
      pages: 'Sayfalar',
      previous: 'Önceki sayfa',
      next: 'Sonraki sayfa',
    },
    period: { label: 'Dönem: {{period}}', d1: 'Bugün', d7: 'Son 7 gün', d30: 'Son 30 gün', d90: 'Son 90 gün' },
    access: {
      directoryInfo: "Kişiler şirket hesabıyla ilk girişte katılır. Roller ve domain'ler şirketin dizininden gelir; burada yalnız erişim açılıp kapatılır.",
      tabsLabel: 'Erişim ve yetkiler bölümleri',
      tabs: { members: 'Üyeler', domains: "Domain'ler", system: 'Sistem hesapları', teams: 'Ekipler' },
      search: 'İsim, e-posta ya da kullanıcı adı ara',
      domain: 'Domain',
      role: 'Rol',
      all: 'Tümü',
      allDomains: 'Tümü ({{count}})',
      roles: { owner: 'Süper yönetici', admin: 'Yönetici', domain_admin: 'Ekip yöneticisi', member: 'Üye' },
      roleFromDirectory: 'Rol şirket dizininden gelir',
      status: { active: 'Aktif', deactivated: 'Erişimi kapalı', inactive: '30 gündür girmeyen' },
      columns: { member: 'Üye', domains: 'Domain', role: 'Rol', lastSignIn: 'Son giriş', status: 'Durum' },
      noun: 'üye',
      nounActive: 'aktif üye',
      lastSignIn: { never: 'Hiç girmedi', today: 'Bugün {{time}}', yesterday: 'Dün {{time}}', days_one: '{{count}} gün önce', days_other: '{{count}} gün önce' },
      menu: {
        label: '{{name}} için işlemler',
        usage: 'Kullanımını ve bütçesini gör',
        audit: 'Denetim kaydında göster',
        sessions: 'Oturumlarını kapat',
        deactivate: 'Erişimi kapat',
        reactivate: 'Erişimi aç',
        remove: 'Firmadan çıkar',
      },
      confirm: {
        sessionsTitle: '{{name}} adlı üyenin oturumları kapatılsın mı?',
        sessionsBody: 'Açık oturumları hemen kapanır; yeniden giriş yapabilir.',
        deactivateTitle: '{{name}} adlı üyenin erişimi kapatılsın mı?',
        deactivateBody: 'Girişi reddedilir ve açık oturumları hemen kapanır. Varsa Microsoft bağlantısı kaldırılır ve anahtarı silinir; başka verisi silinmez. Erişimini istediğinizde yeniden açabilirsiniz.',
      },
      done: { sessions_one: '{{count}} oturum kapatıldı.', sessions_other: '{{count}} oturum kapatıldı.' },
      domains: {
        search: 'Domain ya da çalışma alanı ara',
        columns: { domain: 'Domain', members: 'Üye', admins: 'Yöneticiler', workspace: 'Çalışma alanı', skills: "Ekip skill'leri" },
        noun: 'domain',
        none: 'Yok',
        more: '+{{count}}',
      },
      system: {
        hint: 'Acil durum yöneticisi bir sistem hesabıdır: şirket girişi kullanılamazken parola ve iki aşamalı doğrulama koduyla girer. Üye listelerinde, sayılarda, bütçe ve kullanım tablolarında yer almaz; işletmecinin komutuyla oluşturulur.',
        columns: { account: 'Hesap', twoFactor: 'İki aşamalı doğrulama', lastUsed: 'Son kullanım', created: 'Oluşturuldu' },
        on: 'Açık',
        off: 'Kapalı',
        never: 'Hiç kullanılmadı',
        empty: 'Acil durum yöneticisi yok.',
      },
      relink: {
        title: 'Onay bekleyen şirket hesapları',
        hint: 'Şirket dizini yeniden kurulunca kişi yeni bir kimlikle gelir ve ikinci bir hesap açılmaz. Bağlarsanız kişi kendi hesabına girer; sohbetleri, belgeleri ve skill’leri onun kalır.',
        detail_one: 'Yeni kullanıcı adı: {{username}} · {{count}} deneme · son: {{when}}',
        detail_other: 'Yeni kullanıcı adı: {{username}} · {{count}} deneme · son: {{when}}',
        link: 'Hesabı bağla',
      },
    },
    usage: {
      tabsLabel: 'Kullanım ve limitler bölümleri',
      tabs: { summary: 'Özet', members: 'Üyeler', workspaces: 'Çalışma alanları', feedback: 'Yanıt değerlendirmeleri' },
      cards: {
        month: 'Bu ay firmanın kullanımı',
        monthDomains: "Bu ay domain'lerinizin kullanımı",
        noMonthLimit: 'token · aylık sınır yok',
        monthLimit: 'token · aylık sınır {{limit}}',
        today: 'Bugün kullanan',
        todayUnit: 'üye',
        full: 'Bütçesi dolan',
        near: 'Sınıra yaklaşan',
        show: 'Üyelerde göster',
      },
      defaults: {
        title: 'Varsayılan bütçeler',
        hint: 'Domain ya da kişi için ayrı bir değer yoksa bunlar geçerlidir.',
        monthly: 'Aylık firma bütçesi',
        noLimit: 'Sınır yok',
        daily: 'Üye başına günlük bütçe',
        dailyUnit: 'istem token’ı',
        platform: 'Platformun değeri ({{business}} iş kullanıcısı, {{developer}} yazılımcı)',
        readOnly: 'Firmanın bütçelerini firma yöneticileri değiştirir; siz kendi domain’lerinizin bütçesini değiştirebilirsiniz.',
      },
      domains: {
        title: "Domain'ler",
        hint: "Ekip yöneticileri yalnız kendi domain'lerinin bütçesini, firma sınırı içinde değiştirebilir.",
        columns: { domain: 'Domain', members: 'Üye', today: 'Bugün', budget: 'Kişi başı günlük bütçe', state: 'Durum' },
        full: '{{count}} doldu',
        near: '{{count}} yaklaştı',
        members: 'Üyeler',
        edit: 'Bütçeyi düzenle',
        editLabel: 'Bütçeyi düzenle: {{name}}',
        footnote: 'Acil durum yöneticisi bir sistem hesabıdır; listelerde ve sayılarda yer almaz.',
      },
      source: { personal: 'Özel', domain: 'Domain', organization: 'Firma', platform: 'Platform' },
      state: { full: 'Doldu', near: 'Yaklaştı' },
      members: {
        search: 'İsim ya da e-posta ara',
        chipsLabel: 'Bütçe durumu',
        chips: { all: 'Tümü', full: 'Dolanlar', near: 'Sınıra yaklaşanlar', custom: 'Özel bütçeli', idle: 'Bugün kullanmayanlar' },
        columns: {
          member: 'Üye',
          domain: 'Domain',
          today: 'Bugün',
          month: 'Bu ay',
          budget: 'Günlük bütçe',
          input: 'Giriş token',
          output: 'Çıkış token',
          calls: 'Model çağrısı',
          runs: 'Çalışma',
          sandbox: 'Kod çalıştırma',
          storage: 'Depolama',
          cost: 'Maliyet',
        },
        columnsMenu: 'Sütunlar',
        noun: 'üye',
        note: 'en çok kullanan üstte',
        select: '{{name}} adlı üyeyi seç',
        selected_one: '{{count}} üye seçildi',
        selected_other: '{{count}} üye seçildi',
        assign: 'Günlük bütçe ata',
        reset: 'Varsayılana döndür',
        clear: 'Seçimi kaldır',
        editLabel: '{{name}} için günlük bütçeyi düzenle',
        bulkSet: '{{count}} üyeye {{value}} günlük bütçe verildi.',
        bulkReset: '{{count}} üyenin bütçesi varsayılana döndü.',
      },
      editor: {
        title: '{{name}} · günlük bütçe',
        usedToday: 'Bugün {{used}} kullandı · sıfırlanma {{time}}',
        inherited: 'Varsayılan · {{value}} ({{source}})',
        from: { domain: 'domain’in değeri', organization: 'firmanın değeri', platform: 'platformun değeri' },
        own: 'Kişiye özel',
        bulkTitle_one: '{{count}} üyeye günlük bütçe',
        bulkTitle_other: '{{count}} üyeye günlük bütçe',
        bulkHint: 'Seçilen üyelerin her biri bu değeri kendi bütçesi olarak alır; her değişiklik denetim kaydına yazılır.',
        domainTitle: '{{name}} · kişi başı günlük bütçe',
        domainInherit: 'Firmanın değeri · {{value}}',
        domainPlatform: 'Platformun değeri · {{business}} / {{developer}}',
        domainOwn: 'Domain’e özel',
        tooBig: 'Bu değer firmanın sınırının üstünde; firma yöneticisi değiştirebilir.',
      },
      workspaces: {
        search: 'Çalışma alanı ara',
        kind: 'Tür',
        kinds: { chat: '{{chatProduct}}', code: '{{codeProduct}}' },
        columns: { workspace: 'Çalışma alanı' },
        total: 'Toplam ({{count}} çalışma alanı)',
        noun: 'çalışma alanı',
        costNote: 'Maliyet, Modeller sayfasındaki token fiyatlarıyla hesaplanır. "Çalışma", bir istekten yanıta kadar geçen bir asistan turudur.',
        noPrices: 'Modellere fiyat girilmediği için maliyet "—" görünür; fiyatları Modeller sayfasından girebilirsiniz. "Çalışma", bir istekten yanıta kadar geçen bir asistan turudur.',
      },
    },
    skills: {
      cards: {
        loads: 'Skill yüklemesi',
        loadsSub: '{{skills}} farklı skill · {{people}} kişi',
        model: 'Model kendisi seçti',
        modelSub: '{{percent}} adıyla çağrıldı (/skill)',
        failed: 'Başarısız yükleme',
        failedSub: '{{percent}} · ayrıntı denetim kaydında',
        unused: 'Hiç kullanılmayan',
        unusedSub: "Herkes ve Ekip skill'i",
      },
      search: 'Skill ara',
      scope: 'Kapsam',
      scopes: { builtin: 'Standart', organization: 'Herkes', team: 'Ekip', personal: 'Kişisel' },
      domainChip: 'Ekip: {{name}}',
      personalRow: "Kişisel skill'ler (toplu)",
      columns: { skill: 'Skill', loads: 'Yükleme', how: 'Nasıl yüklendi', failed: 'Başarısız', users: 'Kişi', tokens: 'Token (skill)', lastUsed: 'Son kullanım' },
      byModel: '{{percent}} model',
      noun: 'skill',
      note: 'en çok yüklenen üstte',
      privacy: 'Konuşma içeriği yer almaz.',
      unusedWarning_one: '{{count}} skill {{days}} gündür hiç kullanılmadı:',
      unusedWarning_other: '{{count}} skill {{days}} gündür hiç kullanılmadı:',
      openList: 'Listeyi aç',
      closeList: 'Listeyi kapat',
      lastUsed: { today: 'Bugün', yesterday: 'Dün', days_one: '{{count}} gün önce', days_other: '{{count}} gün önce' },
    },
    audit: {
      hint: 'Giriş, erişim, bütçe, model, bağlantı, lisans ve sistem olayları tek yerde. Konuşma içeriği ve bağlantılara gönderilen veriler burada yer almaz.',
      search: 'Kişi, olay ya da ayrıntı ara',
      person: 'Kişi',
      everyone: 'Herkes',
      chosen: 'Seçili kişi',
      personSearch: 'Kişi ara',
      date: 'Tarih',
      dates: { d1: 'Bugün', d7: 'Son 7 gün', d30: 'Son 30 gün', d90: 'Son 90 gün', d0: 'Tümü' },
      warnings: 'Yalnız uyarılar',
      csv: 'CSV (son 90 gün)',
      columns: { time: 'Saat', event: 'Olay', who: 'Kim', kind: 'Tür', details: 'Ayrıntı' },
      today: 'Bugün · {{date}}',
      yesterday: 'Dün · {{date}}',
      noun: 'olay',
      note: 'en yeni üstte',
      tabsLabel: 'Olay türleri',
      tabs: { all: 'Tümü', access: 'Giriş ve erişim', budgets: 'Bütçeler', models: 'Modeller', connections: 'Bağlantılar', license: 'Lisans', system: 'Sistem' },
      kinds: { access: 'Giriş ve erişim', budgets: 'Bütçeler', models: 'Modeller', license: 'Lisans', system: 'Sistem', skills: "Skill'ler", integrations: 'Bağlantılar' },
      titles: {
        firstLogin: 'İlk giriş · hesap oluşturuldu',
        dailyBudget: 'Günlük bütçe değişti',
        monthlyBudget: 'Aylık bütçe değişti',
        internalOn: 'İç ağ adreslerine izin verildi',
        internalOff: 'İç ağ adreslerine izin kapatıldı',
        // Phase 16c: a backup by how it started; an installation's first start
        nightlyBackup: 'Gece yedeği alındı',
        nightlyBackupFailed: 'Gece yedeği alınamadı',
        installed: 'Oppy kuruldu',
      },
      method: { directory: 'Şirket hesabıyla', password: 'E-posta ve parolayla', break_glass: 'Acil durum girişi', local: 'E-posta ve parolayla' },
      remember: 'beni hatırla: {{value}}',
      linked: 'yerel hesaba bağlandı',
      reason: {
        invalid: 'Yanlış şifre',
        locked: 'Bekleme süresi dolmadan denendi',
        unavailable: 'Giriş servisine ulaşılamadı',
        deactivated: 'Erişimi kapalı',
        totp: 'Doğrulama kodu yanlış',
      },
      // Phase 16c.3: a skill installed from a source, or one that could not be installed or updated, and why
      skill: {
        reason: {
          skill_too_large: 'Skill çok büyük ({{size}})',
          skill_too_large_files: 'Skill çok büyük ({{files}} dosya)',
          skill_source_rate_limited: 'GitHub istek sınırı',
          rateUntil: 'GitHub istek sınırı · {{time}}{{to}} kadar',
          skill_source_not_allowed: 'İzinli sitelerde yok ({{hosts}})',
          denied: 'Engelli sitelerde ({{hosts}})',
          skill_source_not_found: 'Bulunamadı ya da gizli',
          skill_source_timeout: 'Zaman aşımı ({{seconds}} sn)',
          skill_source_network: 'Ağ hatası',
          skill_no_skill_md: 'SKILL.md yok',
          skill_source_unavailable: 'Kaynağa ulaşılamadı',
          unavailableStatus: 'Kaynak {{status}} cevabı verdi',
          skill_invalid: 'Skill dosyası okunamadı',
          payload_too_large: 'Dosya çok büyük',
          skill_external_disabled: 'Dış kaynaklar kapalı',
          name_taken: 'Bu adda bir skill var',
          provider_url_blocked: 'İç ağ adresi',
          other: 'Kurulamadı ({{code}})',
        },
      },
      connections: {
        scopes: { tenant: 'Herkes', workspace: 'Çalışma alanı', user: 'Kişisel' },
        configChanged: 'adres ya da ayarlar değişti',
        policy: { ask: 'Her seferinde sor', allow: 'Sormadan çalıştır', deny: 'Kapalı' },
        method: { oauth: 'oturum açarak', key: 'anahtarla' },
        shared: 'herkes için',
        auto: 'Sorulmadı: {{reason}}',
        approved: 'Onaylandı (bir kez)',
        approvedAlways: 'Onaylandı (kalıcı izinle)',
        rejected: 'Reddedildi',
        denied: 'Engellendi: {{reason}}',
        reasons: {
          trusted_read: 'güvenilir bağlantı, salt okunur',
          tool_policy: 'yönetici izin verdi',
          rule: 'kalıcı izin',
          full_access: 'tam erişim',
          plan_mode: 'plan kipi',
          rule_deny: 'kural',
          other: 'diğer',
        },
        result: { ok: 'başarılı', error: 'hata' },
        seconds: '{{seconds}} sn',
        helper: 'yardımcı',
        conversation: 'konuşma {{id}}',
      },
      attempt: '{{count}}. deneme',
      refreshRefused: 'Şirket girişi oturumu yenilemedi',
      domainsAdded: 'Eklendi: {{domains}}',
      domainsRemoved: 'Çıkarıldı: {{domains}}',
      via: { login: 'girişte dizinden', refresh: 'yenilemede dizinden' },
      budget: {
        monthly: 'Aylık firma bütçesi: {{from}} → {{to}}',
        memberDefault: 'Üye başına varsayılan: {{from}} → {{to}}',
        domain: '{{domain}} varsayılanı: {{from}} → {{to}}',
        member: '{{name}} · kişiye özel: {{from}} → {{to}}',
        bulk_one: 'toplu, {{count}} üye',
        bulk_other: 'toplu, {{count}} üye',
        members_one: '{{count}} üyenin bütçesi',
        members_other: '{{count}} üyenin bütçesi',
        noLimit: 'Sınır yok',
        platform: 'Platform',
        organization: 'Firma',
        default: 'Varsayılan',
      },
      exceeded: { daily: 'Günlük bütçe: {{used}} / {{limit}}', monthly: 'Aylık firma bütçesi: {{used}} / {{limit}}' },
      someone: 'Bir üye',
      sessions_one: '{{count}} oturum kapatıldı',
      sessions_other: '{{count}} oturum kapatıldı',
      newUsername: 'Yeni kullanıcı adı: {{username}}',
      lastDays_one: 'Son {{count}} gün',
      lastDays_other: 'Son {{count}} gün',
      rows_one: '{{count}} satır',
      rows_other: '{{count}} satır',
      lists: { members: 'Üye listesi', usage_members: 'Üyelerin kullanımı', usage_workspaces: 'Çalışma alanlarının kullanımı' },
      skillFailure: { too_large: 'çok büyük', no_room: 'bağlamda yer yok', not_found: 'bulunamadı', disabled: 'kapalı' },
      demoPeople_one: '{{count}} kişi',
      demoPeople_other: '{{count}} kişi',
      demoDomains_one: '{{count}} domain',
      demoDomains_other: '{{count}} domain',
      trialLeft_one: '{{count}} gün kala',
      trialLeft_other: '{{count}} gün kala',
      trialEnded: { expired: 'süresi doldu', license: 'lisans başladı' },
      models: {
        unreachable: 'bağlantı kurulamadı',
        found_one: 'bağlantı denendi, {{count}} model',
        found_other: 'bağlantı denendi, {{count}} model',
        withKey: 'anahtarlı',
        renamed: 'eski adı "{{from}}"',
        address: 'adres: {{address}}',
        addressChanged: 'adresi değişti',
        addressFrom: 'adres: {{from}} → {{address}}',
        unset: 'otomatik',
        on: 'açık',
        off: 'kapalı',
        keyReplaced: 'anahtarı değişti',
        keyCleared: 'anahtarı silindi',
        turnedOn: 'açıldı',
        turnedOff: 'kapatıldı',
        testOk: 'bağlantı çalışıyor',
        testFailed: 'bağlantı kurulamadı',
        testModel: 'model {{model}}',
        refreshed: '{{added}} yeni, {{missing}} kaldırılmış model',
        fields: {
          displayName: 'görünen ad',
          enabled: 'açık ya da kapalı',
          contextWindowOverride: 'bağlam penceresi',
          maxOutputTokens: 'en uzun yanıt',
          priceInputMicros: 'giriş maliyeti',
          priceCachedInputMicros: 'önbellek maliyeti',
          priceOutputMicros: 'çıkış maliyeti',
          thinking: 'düşünme',
          supportsTools: 'araç çağırma',
          description: 'açıklama',
          kind: 'tür',
          chatOffered: 'sohbette',
          chatAudience: 'kimler kullanabilir',
          teams: 'ekipler',
        },
        audiences: { everyone: 'Herkes', teams: 'Seçilen ekipler' },
        mainModel: 'Ana model',
        none: 'Atanmadı',
        internalOn: 'Sağlayıcılar iç ağ adreslerine de bağlanabilir',
        internalOff: 'Sağlayıcılar artık yalnız dış adreslere bağlanabilir',
      },
      system: {
        backup: { both: 'Veritabanı ve dosyalar', database: 'Veritabanı', files: 'Dosyalar' },
        kept_one: '{{count}} yedek saklanıyor',
        kept_other: '{{count}} yedek saklanıyor',
        backupReason: { disk_full: 'diskte yer yok', dump_failed: 'veritabanı yedeklenemedi', files_failed: 'dosyalar yedeklenemedi' },
        manual: 'Elle başlatıldı',
        first: 'Kurulumdan sonra ilk yedek',
        firstInstall: 'ilk kurulum',
        mailTo: 'alıcı {{to}}',
        mailKind: 'hata: {{kind}}',
        installed: '{{version}} kuruldu',
        migrated: 'göçler uygulandı',
        healthy: 'sağlık denetimi geçti',
        noAnswer: 'bağlantı kurulamıyor',
        lasted: '{{duration}} sürdü',
        retried_one: '{{count}} istek yeniden denendi',
        retried_other: '{{count}} istek yeniden denendi',
        failedCalls_one: '{{count}} istek yanıtlanamadı',
        failedCalls_other: '{{count}} istek yanıtlanamadı',
      },
    },
  },
  organization: {
    new: {
      title: 'Yeni firma',
      hint: 'Ekibiniz için bir firma açın; süper yöneticisi siz olursunuz ve üyeleri davet edebilirsiniz. Kişisel alanınız olduğu gibi kalır.',
      needVerified: 'Firma açmak için önce e-posta adresinizi doğrulayın.',
      name: 'Firmanın adı',
      submit: 'Firma oluştur',
    },
    adminHint: '{{name}} firmasını Erişim ve yetkiler sayfasından yönetirsiniz: üyeler, davetler ve ekipler.',
    yourRole: 'Rolünüz: {{role}}',
    yourDomains: "Domain'leriniz: {{domains}}",
  },
  models: {
    tabsLabel: 'Modeller bölümleri',
    tabs: { providers: 'Sağlayıcılar ve görevler', capacity: 'Kapasite' },
    windowAssumed: 'Sağlayıcı bildirmedi; bu bir varsayım. Ayrıntılardan düzeltebilirsiniz.',
    notChecked: 'Henüz denetlenmedi',
    tools: 'Araç çağırma',
    thinking: 'Düşünme',
    vision: 'Görsel',
    missing: 'Sağlayıcıda artık yok',
    disabled: 'Kapalı',
    check: 'Uyumluluğu denetle',
    kinds: { chat: 'Sohbet', embedding: 'Gömme', rerank: 'Yeniden sıralama', ocr: 'Belge okuma', guard: 'Güvenlik' },
    // 16c.1 (B2cModelYeni, KurumsalModelSohbet): "Sohbette", the kind's guess, the duplicate warning, who may use it
    offer: {
      new: 'Yeni',
      displayName: 'Görünen ad: {{name}}',
      duplicate: '{{name}} ile aynı model görünüyor; ikisini birden açmayın',
      guessed: 'adından tahmin',
      kindOf: '{{name}}: tür',
      kindFixed: 'Bir görevde kullanılıyor; türünü değiştirmek için önce o göreve başka bir model seçin.',
      notChat: 'Sohbet modeli değil',
      switchOf: '{{name}}: sohbette',
      on: 'Açık',
      off: 'Kapalı',
      keepPlan: 'Bir pakette. Sohbetten kaldırmak için önce Paketler’den çıkarın.',
      keepTask: 'Sohbet görevinin modeli herkese açık kalır. Kapatmak için önce Görevlere göre model’den başka bir model seçin.',
      windowAssumed: 'Bağlam bilinmiyor',
      checking: 'Uyumluluk denetleniyor…',
      audience: 'Kimler kullanabilir',
      audienceOf: '{{name}}: {{who}}',
      everyone: 'Herkes',
      everyoneHint: 'Firmadaki bütün kullanıcılar',
      chosenTeams: 'Seçilen ekipler',
      chosenTeamsHint: 'Yalnız bu ekiplerin üyeleri seçicide görür',
      teamsCount_one: '{{count}} ekip',
      teamsCount_other: '{{count}} ekip',
      people_one: '{{count}} kişi',
      people_other: '{{count}} kişi',
      noTeams: 'Henüz ekip yok.',
      apply: 'Uygula',
      added: '{{name}} eklendi · {{found}} model bulundu ({{added}} yeni).',
      addedNone: '{{name}} eklendi. Bağlantıyı test edin ya da modelleri yenileyin.',
      addedPlans: 'Yeni modeller kapalı başlar; kişiler görmez. Sohbette sunmak istediklerinizi açın, hangi pakette olacaklarını Paketler’den seçin. Gömme, yeniden sıralama, belge okuma ve güvenlik modelleri sohbete açılamaz; yalnız görevlerde kullanılır.',
      noteLead: 'Sohbette hangi modeller seçilebilir, siz belirlersiniz.',
      note: 'Yeni eklenen modeller kapalı başlar. Açtığınız modeli herkese ya da yalnız seçtiğiniz ekiplere açabilirsiniz; kişi yalnız kendisine açık modelleri seçicide görür. Gömme, sıralama ve güvenlik modelleri sohbete açılamaz.',
    },
    scopes: { platform: 'Platform', tenant: 'Firma', user: 'Kişisel' },
    providerKinds: { openai: 'OpenAI', openai_compatible: 'OpenAI uyumlu', custom: 'Elle eklenen model' },
    list: {
      title: 'Sağlayıcılar',
      providers_one: '{{count}} sağlayıcı',
      providers_other: '{{count}} sağlayıcı',
      models_one: '{{count}} model',
      models_other: '{{count}} model',
      offered_one: 'sohbette {{count}}',
      offered_other: 'sohbette {{count}}',
    },
    table: {
      caption: '{{name}} modelleri',
      model: 'Model',
      kind: 'Tür',
      usage: 'Kullanıldığı yer',
      window: 'Bağlam',
      chat: 'Sohbette',
      capabilities: 'Yetenekler',
      actions: 'İşlemler',
      more: '{{name}}: diğer işlemler',
      moreTasks_one: '+{{count}} görev',
      moreTasks_other: '+{{count}} görev',
    },
    test: {
      ok: 'Bağlantı çalışıyor.',
      failed: 'Bağlantı denetimi başarısız.',
      checks: { models: 'Model listesi', chat_stream: 'Akışlı yanıt', tool_calls: 'Araç çağırma', thinking: 'Düşünme', tokenize: 'Token sayımı' },
      warnings: {
        insecure_transport: 'Bağlantı şifresiz (http). Üretimde https kullanın.',
        context_window_unknown: 'Sağlayıcı bağlam penceresini bildirmiyor; modelin ayrıntılarından girin.',
      },
    },
    details: {
      title: 'Ayrıntılar',
      dialogTitle: '{{name}} · ayrıntılar',
      displayName: 'Görünen ad',
      displayNameHint: 'Konuşmalarda ve listelerde bu ad görünür; boşsa teknik kimlik: {{id}}.',
      description: 'Kısa açıklama',
      descriptionHint: 'Model seçicide adın altında görünür (ör. "dengeli, her iş için").',
      window: 'Bağlam penceresi (token)',
      windowHint: 'Boşsa sağlayıcının bildirdiği değer: {{detected}}.',
      maxOutput: 'En uzun yanıt (token)',
      priceIn: 'Maliyet: 1 milyon giriş token’ı',
      priceOut: 'Maliyet: 1 milyon çıkış token’ı',
      priceHint: 'Kullanım sayfasındaki maliyet bu değerlerle hesaplanır; kendi sunucunuz için 0 bırakın.',
      thinking: 'Düşünme',
      tools: 'Araç çağırma',
      toolsUnknown: 'Bilinmiyor',
      toolsYes: 'Destekliyor',
      toolsNo: 'Desteklemiyor',
      disable: 'Modeli kapat',
      enable: 'Modeli aç',
    },
    thinkingModes: {
      unknown: 'Bilinmiyor',
      none: 'Düşünmüyor',
      template_toggle: 'Açılıp kapatılabilir',
      effort_toggle: 'Düşünme düzeyi seçilebilir',
      always: 'Her zaman düşünüyor',
    },
    provider: {
      connected_one: 'Bağlı · {{count}} model',
      connected_other: 'Bağlı · {{count}} model',
      error: 'Bağlantı hatası',
      untested: 'Test edilmedi',
      keySaved: 'Anahtar kayıtlı',
      noKey: 'Anahtar yok',
      testedAt: 'Son test {{date}}',
      test: 'Bağlantıyı test et',
      refresh: 'Modelleri yenile',
      refreshed: '{{added}} yeni model, {{missing}} kaldırılmış model.',
      noModels: 'Henüz model yok. Bağlantıyı test edin ya da modelleri yenileyin.',
      more: '{{name}}: diğer işlemler',
      edit: 'Düzenle',
      editTitle: 'Sağlayıcıyı düzenle',
      enabled: 'Sağlayıcı açık',
      enabledHint: 'kapalıyken modelleri hiçbir görevde kullanılmaz.',
      deleteTitle: '"{{name}}" silinsin mi?',
      deleteBody_one: 'Sağlayıcı ve {{count}} modeli kaldırılır; anahtarı silinir.',
      deleteBody_other: 'Sağlayıcı ve {{count}} modeli kaldırılır; anahtarı silinir.',
      deleteTasks_one: 'Bu görev onun modelini kullanıyor: {{tasks}}. Silinirse başka bir model seçilene kadar çalışmaz.',
      deleteTasks_other: 'Bu {{count}} görev onun modellerini kullanıyor: {{tasks}}. Silinirse başka bir model seçilene kadar çalışmazlar.',
      deleteNoTasks: 'Hiçbir görev bu sağlayıcının modellerini kullanmıyor.',
    },
    add: {
      title: 'Sağlayıcı ekle',
      kind: 'Tür',
      name: 'Adı',
      namePlaceholder: 'Yedek GPU',
      baseUrl: 'Adres',
      baseUrlHint: 'OpenAI uyumlu sunucunun /v1 ile biten adresi (vLLM, LiteLLM…).',
      openaiHint: 'Boş bırakırsanız OpenAI’ın adresi kullanılır. Dış bulut servislerine gönderilen içerik şirket dışına çıkar.',
      modelId: 'Modelin adı',
      key: 'API anahtarı',
      keyPlaceholder: 'Anahtar yoksa boş bırakın',
      keyHint: 'Kaydedildikten sonra bir daha gösterilmez.',
      newKey: 'Yeni API anahtarı',
      newKeyHint: 'Kayıtlı anahtar gösterilmez; yazdığınız anahtar onun yerini alır.',
      clearKey: 'Anahtarı sil',
      clearKeyHint: 'Sağlayıcı anahtarsız çağrılır.',
      capacityManaged: 'GPU kapasitesini yönet',
      capacityManagedHint: 'Aynı anda giden istekler sunucunun yüküne göre ayarlanır (vLLM).',
      logOptOut: 'İçerik kaydı istemeyin',
      logOptOutHint: 'Sağlayıcıdan istek içeriğini kaydetmemesi istenir (LiteLLM).',
      test: 'Bağlantıyı dene',
      testOk: 'Bağlantı başarılı.',
      testFound_one: 'Bağlantı başarılı · {{count}} model bulundu: ',
      testFound_other: 'Bağlantı başarılı · {{count}} model bulundu: ',
      submit: 'Ekle',
    },
    routes: {
      title: 'Görevlere göre model',
      caption: {
        installation: 'Her görevde hangi modelin çalışacağı.',
        platform: 'Platformun seçimi. Firmalar kendi modelleriyle değiştirebilir.',
        tenant: 'Firmanın seçimi; seçmediğiniz görevlerde platformunki geçerli.',
      },
      task: 'Görev',
      model: 'Model',
      choose: '{{task}} için model',
      none: 'Atanmadı',
      mainModel: 'Ana model · {{name}}',
      viaHelper: 'Diğer küçük işlerin modeli · {{name}}',
      above: { platform: 'Platformun seçimi · {{name}}', tenant: 'Firmanın seçimi · {{name}}' },
      groups: {
        main: { title: 'Ana işler', hint: 'kullanıcının doğrudan gördüğü yanıtlar' },
        helpers: { title: 'Yardımcı işler', hint: 'küçük bir model seçerseniz ana GPU’nun yükü azalır' },
        search: { title: 'Belge araması', hint: 'belgelerde anlamca arama' },
      },
      tasks: {
        main: 'Sohbet',
        code: 'Kod agent’ı',
        subagent: 'Alt agent’lar',
        helper: 'Diğer küçük işler',
        title: 'Başlıklar',
        compaction: 'Konuşma özeti',
        summary: 'Özetler',
        embedding: 'Belge gömme',
        rerank: 'Yeniden sıralama',
        ocr: 'Belge okuma (OCR)',
      },
      hints: {
        main: '{{chatProduct}} sohbetleri',
        code: '{{codeProduct}} çalışma alanları',
        subagent: 'ana agent’ın başlattığı yardımcılar',
        helper: 'açıklama ve benzeri',
        title: 'konuşma başlıkları',
        compaction: 'uzayan konuşmanın özetlenmesi',
        summary: 'belge ve sayfa özetleri',
        embedding: 'belgelerin anlam araması',
        rerank: 'arama sonuçlarının sıralanması',
        ocr: 'taranmış belgelerdeki yazının okunması',
      },
    },
  },
  capacity: {
    period: 'Dönem: {{day}}',
    days: { today: 'Bugün', yesterday: 'Dün' },
    server: {
      title: 'Model sunucusu',
      status: { ok: 'Çalışıyor', errors: 'Hatalarla çalışıyor', down: 'Ulaşılamıyor', idle: 'Boşta' },
      calls_one: 'son 15 dk’da {{count}} çağrı, {{errors}} hata',
      calls_other: 'son 15 dk’da {{count}} çağrı, {{errors}} hata',
      table: 'Sunucu',
      gpu: { title: 'GPU belleği', hint: '{{model}} modeli' },
      memory: { title: 'Sunucu belleği', hint: 'Oppy ve diğer servisler' },
      disk: { title: 'Disk', database: 'veritabanı {{size}}', files: 'dosyalar {{size}}', backups: 'yedekler {{size}}', free: '{{size}} boş' },
      queues: { title: 'Bekleyen işler', hint: 'sohbet {{interactive}} · arka plan {{background}} · belge işleme {{documents}}' },
      sandboxes: { title: 'Açık kod kutuları', hint: 'sohbet {{chat}} · {{codeProduct}} {{code}}' },
      backup: {
        title: 'Son yedek',
        nightly: 'her gece',
        kept_one: 'her gece, {{count}} yedek saklanıyor',
        kept_other: 'her gece, {{count}} yedek saklanıyor',
        none: 'gece yedeği henüz kurulmadı',
        today: 'Bugün {{time}}',
        yesterday: 'Dün {{time}}',
      },
    },
    users: { title: 'Şu an etkin kullanıcı', limit: 'ölçülen sınır {{low}}–{{high}} · son 15 dk' },
    code: { title: 'Çalışan kod işi', limit: 'ölçülen sınır {{low}}–{{high}}' },
    ttft: {
      title: 'İlk yanıt süresi',
      value: '{{value}} sn',
      line: 'çoğunlukla (p50) · en yavaş %5’te (p95) {{p95}} sn',
      none: 'son 15 dakikada yanıt yok',
    },
    verdict: { enough: 'Kapasite yeterli.', close: 'Kapasite sınıra yaklaşıyor.', over: 'Kapasite aşıldı.' },
    rule: 'Etkin kullanıcı sık sık {{low}}’yi geçerse ya da ilk yanıt süresi (p95) 5 saniyeyi aşarsa ikinci bir GPU ya da daha küçük bir yardımcı model düşünün.',
    chart: {
      title: { today: 'Bugün saat saat etkin kullanıcı', yesterday: 'Dün saat saat etkin kullanıcı' },
      peak: 'En yoğun: {{hour}}, {{count}} kişi',
      empty: 'Bu gün model çağrısı yok',
      band: 'Ölçülen sınır: {{low}}–{{high}} etkin kullanıcı',
      people_one: '{{count}} kişi',
      people_other: '{{count}} kişi',
      hourValue_one: '{{hour}}: {{count}} kişi',
      hourValue_other: '{{hour}}: {{count}} kişi',
    },
  },
  platform: {
    intro: 'Bütün firmaların ortak sağlayıcıları, modelleri, kapasitesi ve platform denetim kaydı.',
    tabsLabel: 'Platform bölümleri',
    title: 'Platform',
    onlyOperators: 'Bu sayfayı yalnız platform işletmecileri açabilir.',
    tabs: { providers: 'Sağlayıcılar ve modeller', tenants: 'Firmalar', capacity: 'Kapasite', audit: 'Denetim kaydı' },
    tenants: {
      title: 'Firmalar',
      hint: 'İç ağdaki sağlayıcı adresleri yalnız işaretlediğiniz firmalar için açıktır; bulut metadata ve platform servisleri her zaman kapalıdır.',
      name: 'Firma',
      members: 'Üye',
      tokens: 'Son 30 gün token',
      search: 'Belge araması',
      internal: 'İç adresler',
      allowInternal: '{{name}} iç ağ adreslerini kullanabilsin',
      searchModes: { hybrid: 'Kelime + anlam', keyword: 'Yalnız kelime' },
    },
    audit: { title: 'Platform denetim kaydı' },
  },
  policies: {
    active: 'Etkin',
    approvals: { title: 'Onaylar ve erişim' },
    flags: {
      requireCodeApproval: { title: 'Sohbette kod çalıştırma onayı', hint: 'açıkken {{chatProductIn}} kod çalışmadan önce kişiden onay istenir.' },
      allowFullAccess: { title: 'Tam erişim kipine izin ver', hint: 'güvenilen kod çalışma alanlarında onay sormadan çalışma.' },
      memoryAllowed: { title: 'Hafıza', hint: 'kapalıyken Oppy kimse için bir şey hatırlamaz.' },
      allowPersonalProviders: { title: 'Üyeler kendi model sağlayıcılarını ekleyebilsin', hint: 'kendi anahtarlarıyla, yalnız kendileri için.' },
    },
    documentSearch: {
      title: 'Belge araması',
      hybrid: {
        title: 'Kelime + anlam',
        hint: 'Belgelerdeki parçalar, anlamlarını sayıya çeviren bir servise (Bulutistan) gönderilir. Soru farklı kelimelerle sorulsa da doğru yer bulunur.',
      },
      keyword: {
        title: 'Yalnız kelime',
        hint: 'Belge metni sunucularımızdan çıkmaz. Arama sorudaki kelimelerle yapılır; eş anlamlı ifadelerle sorulan sorularda daha az isabetlidir.',
      },
      confirm: 'Yalnız kelimeye geçince kayıtlı anlam dizinleri hemen silinir. Sonra yeniden açarsanız belgeler arka planda yeniden dizinlenir.',
      switch: 'Yalnız kelimeye geç',
    },
    // D-253: why documents have no vectors; the provider's phrase after the status stays as it says it ("500 · Connection error")
    documentVectors: {
      paused: { title: 'Belgeler anlam dizinine alınamıyor' },
      documents: {
        title_one: '{{count}} belge anlam dizinine alınamadı',
        title_other: '{{count}} belge anlam dizinine alınamadı',
      },
      problem: '{{problem}} ({{detail}}).',
      problemPlain: '{{problem}}.',
      pausedHint: 'Belgeler bu sırada kelimeyle aranıyor; servis yanıt verince anlam araması kendiliğinden geri gelir.',
      waiting_one: '{{count}} belge sırada bekliyor.',
      waiting_other: '{{count}} belge sırada bekliyor.',
      documentsHint: 'Her biri kendi sırasıyla yeniden denenecek; bu sırada kelimeyle aranabiliyorlar.',
      attempts: 'Son deneme {{last}} · sonraki {{next}}',
      attemptsSince_one: 'Son deneme {{last}} · sonraki {{next}} · {{count}} deneme, ilk hata {{since}}',
      attemptsSince_other: 'Son deneme {{last}} · sonraki {{next}} · {{count}} deneme, ilk hata {{since}}',
      probing: 'Son deneme {{last}} · şu an bir belgeyle yeniden deneniyor',
      soon: 'birazdan',
      indexing_one: '{{count}} belge anlam dizinine alınıyor.',
      indexing_other: '{{count}} belge anlam dizinine alınıyor.',
      retry: 'Şimdi yeniden dene',
      retried: 'Bekleyen belgeler bir dakika içinde yeniden denenecek.',
      problems: {
        not_answering: 'Gömme servisi yanıt vermiyor',
        unreachable: 'Gömme servisine bağlanılamıyor',
        auth: 'Gömme servisi anahtarı kabul etmiyor',
        model: 'Gömme modeli bulunamıyor',
        no_model: 'Belge gömme için model seçilmemiş',
        quota: 'Kullanım sınırı doldu',
        blocked: 'Gömme servisinin adresi engelli',
        gateway: 'Oppy’nin model geçidine ulaşılamıyor',
        rejected: 'Gömme servisi bu belgelerin metnini kabul etmiyor',
        invalid: 'Gömme servisinden geçersiz yanıt geliyor',
        other: 'Gömme servisi hata veriyor',
      },
    },
    models: {
      title: 'Sohbet modelleri',
      moved: 'Üyelerin sohbette seçebileceği modelleri ve her birini kimlerin kullanabileceğini artık Yönetim › Modeller’de, her modelin “Sohbette” ve “Kimler kullanabilir” ayarıyla seçersiniz. Sohbet görevinin modeli herkese açıktır.',
      open: 'Modeller’e git',
    },
    intro: 'Onaylar, belge araması ve web için firmanın kuralları; sohbet modelleri Modeller’de.',
  },
  audit: {
    hint: 'Yönetim ve güvenlik olayları. Konuşma içeriği burada yer almaz.',
    filter: 'Göster',
    filters: { all: 'Tümü', tenant: 'Firma ve üyeler', quota: 'Bütçeler', mcp: 'Bağlantılar', platform: 'Platform', audit: 'Dışa aktarmalar' },
    export: 'CSV olarak indir (son 90 gün)',
    empty: 'Kayıt yok.',
    system: 'Sistem',
    more: 'Daha eski kayıtlar',
    columns: { time: 'Zaman', action: 'Olay', actor: 'Kim', details: 'Ayrıntı' },
    actions: {
      tenant_created: 'Firma oluşturuldu',
      tenant_renamed: 'Firmanın adı değişti',
      tenant_full_access_changed: 'Tam erişim izni değişti',
      tenant_settings_changed: 'Firma ayarları değişti',
      tenant_member_role_changed: 'Üyenin rolü değişti',
      tenant_member_removed: 'Üye çıkarıldı',
      tenant_member_left: 'Üye ayrıldı',
      tenant_invite_created: 'Davet gönderildi',
      tenant_invite_accepted: 'Davet kabul edildi',
      tenant_document_search_changed: 'Belge araması değişti',
      tenant_document_vectors_retried: 'Anlam dizini için yeniden denendi',
      tenant_models_allowed_changed: 'İzin verilen modeller değişti',
      tenant_personal_providers_changed: 'Kişisel sağlayıcı izni değişti',
      quota_exceeded: 'Token bütçesi doldu',
      quota_changed: 'Bütçeler değişti',
      audit_exported: 'Denetim kaydı indirildi',
      mcp_oauth_client_set: 'Bağlantının giriş istemcisi kaydedildi',
      mcp_oauth_client_cleared: 'Bağlantının giriş istemcisi silindi',
      platform_internal_providers_changed: 'İç ağ adresi izni değişti',
      platform_provider_created: 'Platform sağlayıcısı eklendi',
      platform_provider_updated: 'Platform sağlayıcısı değişti',
      platform_provider_deleted: 'Platform sağlayıcısı silindi',
      platform_models_refreshed: 'Platform modelleri yenilendi',
      platform_model_updated: 'Platform modeli değişti',
      platform_route_changed: 'Platformun görev ataması değişti',
      approval_decided: 'Onay kartı yanıtlandı',
      approval_mode_changed: 'Onay kipi değişti',
      auth_provider_created: 'Şirket giriş servisi bağlandı',
      auth_provider_changed: 'Şirket giriş servisinin ayarları değişti',
      auth_link_requested: 'Yerel hesap şirket girişine bağlanmak üzere işaretlendi',
      auth_account_linked: 'Yerel hesap şirket girişine bağlandı',
      tenant_converted: 'Kişisel alan firmaya dönüştürüldü',
      auth_login_failed: 'Giriş başarısız',
      auth_break_glass_login: 'Acil durum yöneticisi girişi',
      auth_break_glass_set: 'Acil durum yöneticisi ayarlandı',
      auth_refresh_refused: 'Şirket girişi yenilenmedi, oturum kapandı',
      auth_role_changed: 'Dizindeki rol değişti',
      auth_domains_changed: "Dizindeki domain'ler değişti",
      tenant_member_deactivated: 'Üyenin erişimi kapatıldı',
      tenant_member_reactivated: 'Üyenin erişimi açıldı',
      identity_domain_renamed: "Domain'in çalışma alanı adı değişti",
      auth_login: 'Giriş yapıldı',
      auth_logout: 'Çıkış yapıldı',
      auth_password_changed: 'Parola değişti',
      document_deleted: 'Belge silindi',
      document_shared: 'Belge paylaşıldı',
      document_uploaded: 'Belge yüklendi',
      mcp_server_created: 'Bağlantı eklendi',
      mcp_server_deleted: 'Bağlantı silindi',
      mcp_server_updated: 'Bağlantı değişti',
      mcp_server_trusted: 'Bağlantıya güvenildi',
      mcp_server_untrusted: 'Bağlantının güveni kaldırıldı',
      permission_rule_revoked: 'İzin kuralı kaldırıldı',
      skill_created: 'Skill eklendi',
      skill_proposed: 'Skill önerildi',
      skill_published: 'Skill yayımlandı',
      skill_rejected: 'Skill reddedildi',
      skill_deleted: 'Skill silindi',
      skill_rolled_back: 'Skill eski sürüme döndü',
      skill_version_added: "Skill'in yeni sürümü",
      team_created: 'Ekip oluşturuldu',
      team_deleted: 'Ekip silindi',
      tenant_web_policy_changed: 'Web politikası değişti',
      user_data_deleted: 'Kullanıcı verilerini sildi',
      workspace_created: 'Çalışma alanı oluşturuldu',
      workspace_deleted: 'Çalışma alanı silindi',
    },
  },
  skillAnalytics: {
    hint: "Hangi skill'lerin kullanıldığı, modelin mi seçtiği yoksa adıyla mı çağrıldığı, başarısız yüklemeler ve maliyet. Konuşma içeriği yer almaz.",
    used: "Kullanılan skill'ler",
    empty: 'Bu dönemde skill kullanılmadı.',
    unused: "Hiç kullanılmayan Herkes ve Ekip skill'leri",
    noneUnused: 'Kullanılmayan skill yok.',
    costHint: "Skill'in bağlama eklediği token / yüklendiği çalışmaların toplam token’ı",
    columns: {
      skill: 'Skill',
      loads: 'Yükleme',
      byModel: 'Model seçti',
      byUser: 'Adıyla çağrıldı',
      failed: 'Başarısız',
      users: 'Kişi',
      cost: 'Token (skill / çalışma)',
      lastUsed: 'Son kullanım',
    },
    private: { user: "Üyelerin kişisel skill'leri", workspace: "Çalışma alanı skill'leri" },
    levels: { builtin: 'Standart', organization: 'Herkes', team: 'Ekip', user: 'Kişisel', workspace: 'Çalışma alanları' },
  },
  integrations: {
    hint: 'Herkes için eklenen bağlantılar. Eklemek ve kaldırmak için:',
    manage: 'Bağlantılar sayfası',
    empty: 'Firmanın henüz bağlantısı yok.',
    modes: { available: 'İsteğe bağlı', default_on: 'Varsayılan açık', required: 'Zorunlu' },
    auth: { none: 'Giriş gerekmiyor', headers: 'Anahtarla', oauth: 'Hesapla giriş (OAuth)' },
    tools_one: '{{count}} araç',
    tools_other: '{{count}} araç',
    oauth: {
      saved: 'Kayıtlı giriş istemcisi: {{clientId}}',
      none: 'Giriş servisi istemci kaydını kendisi yapmıyorsa, servise önceden kaydettiğiniz istemciyi girin.',
      secretSaved: 'Gizli anahtar kayıtlı',
      done: 'Giriş istemcisi kaydedildi. Üyeler bir sonraki girişte bunu kullanır.',
      clientId: 'İstemci kimliği (client ID)',
      secret: 'İstemci gizli anahtarı (client secret)',
      secretHint: 'Yalnız şimdi görünür; kaydedildikten sonra gösterilmez. Değiştirmeyecekseniz boş bırakın.',
      clear: 'İstemciyi kaldır',
    },
  },
  notFound: { title: 'Sayfa bulunamadı', body: 'Adres yanlış olabilir ya da sayfa kaldırılmış olabilir.', back: 'Ana sayfaya dön' },
  // v0.15a.3: a page or a panel that could not be drawn
  crash: {
    title: 'Bir sorun oldu',
    body: 'Sayfa beklenmedik bir hatayla durdu. Yenilemek çoğu zaman düzeltir.',
    panelBody: 'Bu bölüm açılamadı; konuşma çalışmaya devam ediyor.',
    reload: 'Yenile',
    home: 'Ana sayfaya dön',
  },
  fields: {
    invalid: 'Bu alanı kontrol edin.',
    email: 'Geçerli bir e-posta adresi yazın.',
    required: 'Bu alan zorunlu.',
  },
  errors: {
    ...m365ErrorsTr,
    document_timeout: {
      title: 'Belge zamanında okunamadı; yanıt başlatılmadı. Belge hazır olunca yeniden deneyin.',
      action: 'Belge çok büyük ya da taranmışsa okunması uzun sürebilir.',
    },
    document_unreadable: {
      title: 'Eklenen belge okunamadı; yanıt başlatılmadı. Dosyayı kontrol edip yeniden ekleyin.',
      action: 'Belgenin bozuk ya da parola korumalı olmadığından emin olun.',
    },
    mcp_test_failed: { title: 'Bağlantıya bağlanılamadı.', action: 'Adresi, komutu ya da anahtarı kontrol edip yeniden deneyin.' },
    mcp_needs_auth: { title: 'Bu bağlantı için oturum açmanız gerekiyor.', action: 'Bağlantılar sayfasında "Oturum aç" düğmesini kullanın.' },
    mcp_not_approved: { title: 'Bu ortak bağlantının yapılandırması onayınızı bekliyor.', action: 'Bağlantılar sayfasında yapılandırmayı inceleyip onaylayın.' },
    mcp_unavailable: { title: 'Bağlantıya şu an ulaşılamıyor.', action: 'Biraz sonra yeniden deneyin; sürerse bağlantıyı yenileyin.' },
    mcp_user_servers_disabled: { title: 'Firmanız kendi bağlantınızı eklemeyi kapattı.', action: 'Herkes bölümündeki bağlantıları kullanın ya da yöneticinize başvurun.' },
    name_taken: { title: 'Bu ad bu düzeyde zaten kullanılıyor.', action: "Başka bir ad seçin ya da mevcut skill'i güncelleyin." },
    skill_invalid: { title: 'Skill dosyası okunamadı.', action: 'Ön bilgide ad ve açıklama olduğunu, talimatların çok uzun olmadığını kontrol edin.' },
    skill_source_unavailable: { title: 'Skill kaynağına ulaşılamadı.', action: 'Adresi kontrol edin; depo gizliyse ZIP olarak yükleyin.' },
    skill_external_disabled: { title: 'Firmanız dış kaynaklardan skill kurmayı kapattı.', action: "Herkes bölümündeki skill'leri kullanın ya da yöneticinize başvurun." },
    skill_choose_folder: { title: 'Bu kaynakta birden çok skill var.', action: 'Listeden kurmak istediğiniz klasörü seçin.' },
    skill_too_large: { title: 'Bu skill çok büyük.', action: 'Örnek dosyaları çıkarıp ZIP olarak yükleyebilirsiniz.' },
    skill_source_rate_limited: { title: 'GitHub bizi bir süre bekletiyor.', action: 'Biraz sonra yeniden deneyin ya da skill’i ZIP olarak yükleyin.' },
    skill_source_not_allowed: { title: 'Bu adrese bağlanma izni yok.', action: 'Yöneticiniz izinli sitelere ekleyebilir. Bu arada skill’i ZIP olarak yükleyebilirsiniz.' },
    skill_source_not_found: { title: 'Bu adres bulunamadı.', action: 'Adresi kontrol edin; gizli bir depoysa skill klasörünü ZIP yapıp yükleyin.' },
    skill_source_timeout: { title: 'Kaynak zamanında cevap vermedi.', action: 'Birkaç dakika sonra yeniden deneyin.' },
    skill_source_network: { title: 'Kaynağa ulaşılamadı.', action: 'Sorun sürerse yöneticinize hata numarasını iletin.' },
    skill_no_skill_md: { title: 'Bu adreste skill bulunamadı.', action: 'Skill’in klasörünü gösteren bağlantıyı yapıştırın.' },
    workspace_untrusted: { title: 'Bu çalışma alanı güvenilir değil.', action: 'Yerel bağlantı ve skill izinleri için güvenilir bir çalışma alanı kullanın.' },
    search_unavailable: { title: 'Web araması şu an kullanılamıyor.', action: 'Biraz sonra yeniden deneyin ya da bilinen bir adresi doğrudan verin.' },
    validation_failed: { title: 'Bazı alanlar eksik ya da hatalı.', action: 'İşaretli alanları düzeltip yeniden deneyin.' },
    unauthenticated: { title: 'Oturumunuz kapanmış.', action: 'Yeniden giriş yapın.' },
    forbidden: { title: 'Bu işlem için yetkiniz yok.', action: 'Gerekirse çalışma alanını oluşturan kişiden ya da bir yöneticiden yetki isteyin.' },
    not_found: {
      title: 'Aradığınız şey bulunamadı.',
      action: 'Bağlantı eski olabilir ya da bu kaynağa erişiminiz olmayabilir. Panoya dönün.',
    },
    conflict: { title: 'Bu işlem şu anki durumla çakışıyor.', action: 'Sayfayı yenileyip yeniden deneyin.' },
    csrf_failed: { title: 'Güvenlik denetimi başarısız oldu.', action: 'Sayfayı yenileyip yeniden deneyin.' },
    client_update_required: { title: "Oppy uygulamasının yeni sürümü gerekli.", action: "TestFlight'tan uygulamayı güncelleyin." },
    client_unknown: { title: 'Bu istek Oppy uygulamasından gelmedi.', action: 'Uygulamayı yeniden açın.' },
    rate_limited: { title: 'Çok fazla deneme yapıldı.', action: 'Biraz bekleyip yeniden deneyin.' },
    invalid_credentials: {
      title: 'E-posta ya da parola hatalı.',
      action: 'Bilgilerinizi kontrol edin. Parolanızı unuttuysanız sıfırlayabilirsiniz.',
    },
    directory_unavailable: { title: 'Şirket giriş servisine şu an ulaşılamıyor.', action: 'Birkaç dakika sonra yeniden deneyin.' },
    account_deactivated: { title: 'Oppy erişiminiz kapatılmış.', action: 'Yöneticinize başvurun.' },
    account_relink_required: {
      title: 'Şirket hesabınız değişmiş.',
      action: 'Yöneticiniz onaylayınca aynı hesapla girebilirsiniz; yöneticinize haber verin.',
    },
    membership_from_directory: {
      title: 'Üyelik şirket dizininden gelir.',
      action: 'Kişiyi çıkarmak yerine erişimini kapatın; ekipler domain’lerden oluşur.',
    },
    email_not_verified: {
      title: 'Önce e-posta adresinizi doğrulayın.',
      action: 'Gelen kutunuzdaki bağlantıyı açın ya da yeni bağlantı isteyin.',
    },
    token_invalid: { title: 'Bağlantı geçersiz, kullanılmış ya da süresi dolmuş.', action: 'Yeni bir bağlantı isteyin.' },
    password_too_short: { title: 'Parola çok kısa.', action: 'En az 10 karakterlik bir parola seçin.' },
    password_too_long: { title: 'Parola çok uzun.', action: 'En fazla 256 karakterlik bir parola seçin.' },
    password_breached: {
      title: 'Bu parola çok yaygın ya da sızdırılmış parola listelerinde var.',
      action: 'Tahmin edilmesi zor başka bir parola seçin.',
    },
    invite_email_mismatch: {
      title: 'Bu davet başka bir e-posta adresine gönderilmiş.',
      action: 'Davetin gönderildiği adresle giriş yapın.',
    },
    already_member: { title: 'Bu kişi zaten ekipte.', action: 'Üye listesini kontrol edin.' },
    last_owner: { title: 'Firmada en az bir süper yönetici kalmalı.', action: 'Önce başka bir üyeyi süper yönetici yapın.' },
    role_not_allowed: { title: 'Bu rolü yalnız süper yönetici verebilir.', action: 'Bir süper yöneticiden yardım isteyin.' },
    project_path_invalid: {
      title: 'Klasör adı geçersiz.',
      action: 'Harf, rakam, nokta, tire ve alt çizgi kullanın; “..” ve baştaki “/” kullanmayın.',
    },
    project_path_nested: {
      title: 'Bu klasör başka bir projenin içinde ya da onu kapsıyor.',
      action: 'İç içe olmayan bir klasör seçin.',
    },
    project_path_taken: { title: 'Bu klasör başka bir projede kullanılıyor.', action: 'Başka bir klasör seçin.' },
    workspace_kind_mismatch: {
      title: 'Bu seçenek sohbet alanlarında yok.',
      action: 'Klasör ve depo yalnız kod çalışma alanlarında kullanılabilir.',
    },
    payload_too_large: { title: 'Gönderilen içerik çok büyük.', action: 'Metni kısaltıp yeniden deneyin.' },
    unsupported_document: {
      title: 'Bu dosya türü belge olarak okunamıyor.',
      action: 'PDF, Word, Excel, PowerPoint, metin, Markdown, CSV ya da görsel (PNG, JPEG) yükleyin.',
    },
    not_ready: { title: 'Belge henüz hazır değil.', action: 'Okuma bitince yeniden deneyin; durum belgenin yanında görünür.' },
    provider_url_invalid: { title: 'Sağlayıcı adresi geçersiz.', action: 'http:// ya da https:// ile başlayan, kullanıcı adı ve parametre içermeyen bir adres yazın.' },
    provider_url_blocked: {
      title: 'Bu adrese bağlanmaya izin verilmiyor.',
      action: 'İnternete açık bir sağlayıcı adresi kullanın. İç ağdaki bir sunucu gerekiyorsa platform yöneticisiyle görüşün.',
    },
    api_key_required: { title: 'Bu sağlayıcı için API anahtarı gerekli.', action: 'Sağlayıcının verdiği anahtarı girip yeniden deneyin.' },
    personal_providers_disabled: {
      title: 'Firmanız kişisel model sağlayıcısı eklemeye izin vermiyor.',
      action: 'Firmanın sağladığı modelleri kullanın ya da yöneticinizden izin isteyin.',
    },
    model_unavailable: {
      title: 'Bu iş için seçilen model şu an kullanılamıyor.',
      action: 'Model kapatılmış ya da sağlayıcıdan kaldırılmış olabilir. Başka bir model seçin ya da yöneticinize haber verin.',
    },
    service_unavailable: { title: 'Hizmet şu an yanıt vermiyor.', action: 'Biraz sonra yeniden deneyin.' },
    no_model_configured: { title: 'Bu çalışma alanı için bir model ayarlı değil.', action: 'Yöneticinizden bir model tanımlamasını isteyin.' },
    context_overflow: { title: 'Konuşma modelin bağlam sınırını aştı.', action: 'Konuşmayı özetleyin (/compress) ya da yeni bir sohbette devam edin.' },
    quota_exceeded: { title: 'Kullanım kotanız doldu.', action: 'Biraz bekleyin ya da yöneticinizle görüşün.' },
    model_not_allowed: { title: 'Bu model firmanızda kullanıma açık değil.', action: 'Başka bir model seçin ya da yöneticinizden bu modele izin vermesini isteyin.' },
    organization_limit: { title: 'Kurabileceğiniz firma sayısının sınırına ulaştınız.', action: 'Destek ekibine yazın.' },
    // Phase 16a: the bireysel edition (model_busy has no words of its own: the generic failure line, D-271)
    file_too_large: { title: 'Bu dosya paketinizin dosya boyutu sınırını aşıyor.', action: 'Daha küçük bir dosya seçin.' },
    document_limit: { title: 'Belge sınırına ulaştınız.', action: 'Belgelerinizden birini silin.' },
    plan_required: { title: 'Bu özellik paketinizde yok.', action: 'Pro’da açılır.' },
    payments_closed: { title: 'Ödemeler henüz açık değil.', action: 'Çok yakında burada açılıyor; o zamana kadar Ücretsiz paketle devam edebilirsiniz.' },
    skill_limit: { title: 'Paketinizin kendi skill sınırına ulaştınız.', action: 'Yenisini eklemek için birini silin ya da Pro’ya geçin; Oppy ile gelen skill’ler sayılmaz.' },
    connection_limit: { title: 'Paketinizin kendi bağlantı sınırına ulaştınız.', action: 'Yenisini eklemek için birini kaldırın ya da Pro’ya geçin.' },
    model_in_plan: { title: 'Bu model bir pakette.', action: 'Önce Paketler’den modeli paketten çıkarın, sonra sohbetten kaldırın ya da türünü değiştirin.' },
    model_in_task: { title: 'Bu model bir görevde kullanılıyor.', action: 'Önce Görevlere göre model’den o göreve başka bir model seçin.' },
    model_kept: { title: 'Abonelerin bu modeli kullanma hakkı sürüyor.', action: 'Pro’dan çıkarılan bir model, abonelere duyurulan değişiklik yürürlüğe girince sohbetten kaldırılabilir.' },
    provider_in_plan: { title: 'Bu sağlayıcının bir modeli bir pakette ya da abonelerin hakkında.', action: 'Önce Paketler’den bu modelleri çıkarın; abonelerin hakkı duyurulan değişiklik yürürlüğe girince biter. Sonra sağlayıcıyı silin.' },
    already_subscribed: { title: 'Pro aboneliğiniz zaten var.', action: 'Ayarlar › Abonelik’ten yönetebilirsiniz.' },
    legal_version_changed: { title: 'Sözleşme metinleri bu sayfa açıldıktan sonra değişti.', action: 'Sayfayı yenileyip metinleri yeniden onaylayın.' },
    scope_not_available: { title: 'Burada yalnız kendi alanınız var.', action: 'Kişisel olarak kaydedin.' },
    code_invalid: { title: 'Kod doğru değil.', action: 'Kodu kontrol edip yeniden girin.' },
    code_expired: { title: 'Kodun süresi doldu.', action: 'Yeni kod isteyin.' },
    registration_unknown: { title: 'Bu kayıt artık geçerli değil.', action: 'Yeniden hesap oluşturun.' },
    provider_unavailable: { title: 'Model sağlayıcısına şu an ulaşılamıyor.', action: 'Biraz sonra yeniden deneyin.' },
    no_visible_reply: { title: 'Model bir yanıt üretemedi.', action: 'Mesajınızı yeniden gönderin ya da daha kısa bir istek deneyin.' },
    run_interrupted: { title: 'Çalışma yarıda kesildi.', action: 'Devam et düğmesiyle sürdürün.' },
    queue_full: { title: 'Bekleyen mesaj sayısı sınıra ulaştı.', action: 'Önceki mesajların işlenmesini bekleyin.' },
    run_not_active: { title: 'Bu çalışma artık sürmüyor.', action: 'Sayfayı yenileyin.' },
    approval_not_pending: { title: 'Bu istek zaten yanıtlanmış.', action: 'Sayfayı yenileyin.' },
    turn_active: { title: 'Bu konuşmada şu an bir yanıt hazırlanıyor.', action: 'Yanıt bitince ya da durdurduktan sonra yeniden deneyin.' },
    rewind_before_compaction: { title: 'Bu nokta özetlenen kısımda kaldığı için oraya geri dönülemiyor.', action: 'Özetten sonraki bir mesajı seçin ya da bu noktadan yeni bir dal açın.' },
    nothing_to_compact: { title: 'Özetlenecek kadar uzun bir konuşma yok.', action: 'Konuşma uzayınca özetleme kendiliğinden yapılır.' },
    compaction_failed: { title: 'Konuşma özetlenemedi.', action: 'Biraz sonra yeniden deneyin; olmazsa yeni bir sohbette devam edin.' },
    nothing_to_regenerate: { title: 'Yeniden üretilecek bir yanıt yok.', action: 'Önce bir mesaj gönderin.' },
    memory_disabled: { title: 'Hafıza kapalı.', action: "Ayarlar > Hafıza'dan açabilirsiniz; firma yöneticiniz kapattıysa ona danışın." },
    mode_not_allowed: { title: 'Bu onay kipi burada kullanılamaz.', action: 'Başka bir kip seçin.' },
    sandbox_unavailable: { title: 'Çalışma ortamı şu an başlatılamadı.', action: 'Biraz sonra yeniden deneyin.' },
    sandbox_limit: { title: 'Aynı anda açık çalışma ortamı sınırına ulaşıldı.', action: 'Kullanmadığınız bir çalışma alanını kapatın ya da biraz bekleyin.' },
    sandbox_error: { title: 'İşlem çalışma ortamında tamamlanamadı.', action: 'Ayrıntılara bakıp yeniden deneyin.' },
    path_outside_workspace: { title: 'Bu konum çalışma alanının dışında.', action: 'Çalışma alanı içindeki bir dosya seçin.' },
    path_not_found: { title: 'Dosya ya da klasör bulunamadı.', action: 'Listeyi yenileyip yeniden deneyin.' },
    read_only: { title: 'Bu dosya yalnız okunabilir.', action: 'Değiştirmek için çalışma alanına bir kopyasını alın.' },
    is_directory: { title: 'Bu bir klasör.', action: 'Bir dosya seçin.' },
    not_a_directory: { title: 'Bu bir klasör değil.', action: 'Bir klasör seçin.' },
    sensitive_path: { title: 'Bu dosya gizli bilgi içerebilir.', action: 'Açmak için ayrıca onay vermeniz gerekir.' },
    disk_quota_exceeded: { title: 'Çalışma alanının disk sınırı doldu.', action: 'Gereksiz dosyaları silip yeniden deneyin.' },
    not_a_git_repo: { title: 'Bu çalışma alanında Git deposu yok.', action: 'Önce Git deposu oluşturun.' },
    git_error: { title: 'Git işlemi tamamlanamadı.', action: 'Ayrıntılara bakıp yeniden deneyin.' },
    port_not_listening: { title: 'Bu portta çalışan bir uygulama yok.', action: 'Uygulamayı başlatıp yeniden deneyin.' },
    preview_unreachable: { title: 'Önizleme açılamadı.', action: 'Uygulamanın çalıştığından emin olup yeniden deneyin.' },
    preview_unavailable: { title: 'Önizleme bu kurulumda kapalı.', action: 'Alan adı bağlanınca açılır; dosyalarınız Dosyalar sekmesinde.' },
    snapshot_not_found: { title: 'Bu geri dönüş noktası artık geçerli değil.', action: 'Listeyi yenileyip başka bir nokta seçin.' },
    snapshot_failed: { title: 'Geri dönüş noktası işlemi tamamlanamadı.', action: 'Biraz sonra yeniden deneyin.' },
    internal_error: {
      title: 'Beklenmeyen bir sorun oluştu.',
      action: 'Yeniden deneyin. Sorun sürerse destek ekibine teknik ayrıntılardaki istek kimliğini iletin.',
    },
    network_error: { title: 'Sunucuya ulaşılamadı.', action: 'İnternet bağlantınızı kontrol edip yeniden deneyin.' },
    license_read_only: {
      title: 'Oppy şu an salt okunur: lisans süresi doldu.',
      action: 'Eski sohbet ve belgeler açılır ve dışa aktarılır; yeni iş başlatılamaz. Yöneticinize haber verin.',
    },
    product_not_licensed: { title: 'Bu ürün lisansınızda yok.', action: 'Lisansa eklenmesi için yöneticinize haber verin.' },
    retryAfter: '{{minutes}} dakika sonra yeniden deneyebilirsiniz.',
  },
  // SA-023: which budget is used up and when it resets; a run that stopped for it (D-97, KotaSohbet.dc.html)
  quotaNotice: {
    user_daily: 'Bugünkü token bütçeniz doldu.',
    tenant_monthly: 'Firmanızın bu ayki token bütçesi doldu.',
    resets: 'Yenilenme zamanı: <b>{{when}}</b>.',
    adminCanRaise: 'Yöneticiniz sınırı yükseltebilir.',
    kept: {
      code: 'Yazılan dosyalar çalışma alanında duruyor; bütçe yenilenince ya da yükseltilince iş kaldığı yerden devam eder.',
      chat: 'Şimdiye kadar hazırlananlar bu sohbette duruyor; bütçe yenilenince ya da yükseltilince iş kaldığı yerden devam eder.',
    },
    usedToday: 'Bugün: {{used}} / {{limit}} istem token’ı',
    usedMonth: 'Bu ay: {{used}} / {{limit}} token',
    still: 'Bütçe hâlâ dolu; yenilenme zamanından sonra ya da bütçe yükseltilince yeniden deneyin.',
    limits: 'Limitler ve kotalar',
  },
  // Phase 13f: the enterprise license (06 §26.4; YonetimLisans, LisansYukle, LisansRozet, LisansSaltOkunur, LisansBildirim)
  license: {
    openPage: 'Lisans sayfasını aç',
    chip: {
      days_one: 'Lisans: {{count}} gün',
      days_other: 'Lisans: {{count}} gün',
      trial_one: 'Deneme: {{count}} gün',
      trial_other: 'Deneme: {{count}} gün',
      readOnly: 'Salt okunur',
      hint: 'Lisans durumu',
    },
    popover: {
      titles: { reminder: 'Lisans bitmek üzere', grace: 'Lisans ek sürede', readOnly: 'Oppy salt okunur', trial: 'Deneme sürümü' },
      reminderBody: 'Lisans {{day}} bitiyor. Yenilemek için {{vendor}} ile görüşün.',
      trialBody: 'Oppy {{day}} kadar ücretsiz ve tam çalışır.',
      trialEndedBody: 'Ücretsiz deneme {{day}} bitti.',
      graceBody_one: 'Lisans {{day}} bitti. Oppy {{count}} gün daha normal çalışır.',
      graceBody_other: 'Lisans {{day}} bitti. Oppy {{count}} gün daha normal çalışır.',
      readOnlyBody: 'Lisans {{day}} bitti ve ek süre doldu.',
      noLicenseBody: 'Bu kurulumda geçerli bir lisans yok.',
      steps: { reminder: 'Hatırlatma', grace: 'Ek süre', readOnly: 'Salt okunur', trial: 'Deneme', license: 'Lisans' },
      now: 'Şimdi',
      from: '{{day}} itibaren',
      reminderNote: 'Bitişten sonra 14 gün ek süre var; o da bitince Oppy salt okunur olur. Verileriniz silinmez.',
      trialNote: 'Devam etmek için kurulum kimliğini {{vendorTo}} gönderin ve gelen lisans dosyasını yükleyin. Verileriniz hiçbir durumda silinmez.',
      trialNoteQueued: 'Yüklediğiniz lisans {{day}} kendiliğinden başlar. Verileriniz hiçbir durumda silinmez.',
      graceNote: '{{day}} itibaren eski sohbetler açılır ama yeni mesaj gönderilemez. Verileriniz silinmez. Lütfen yöneticinize haber verin.',
      graceNoteAdmin: '{{day}} itibaren eski sohbetler açılır ama yeni mesaj gönderilemez. Verileriniz silinmez. Yeni lisans dosyasını Lisans sayfasından yükleyin.',
      readOnlyNote: 'Eski sohbet ve belgelerinizi açıp dışa aktarabilirsiniz; yeni mesaj gönderilemez. Verileriniz silinmedi. Lütfen yöneticinize haber verin.',
      readOnlyNoteAdmin: 'Eski sohbet ve belgeler açılır ve dışa aktarılır; yeni mesaj gönderilemez. Yeni lisans dosyası yüklenince her şey hemen eski haline döner.',
    },
    facts: {
      owner: 'Lisans sahibi',
      products: 'Ürünler',
      validity: 'Geçerlilik',
      licenseId: 'Lisans no',
      installation: 'Kurulum kimliği',
      users: 'Kullanıcı',
      usersFull: 'Sınırsız · şirket lisansı',
      usersTrial: 'Sınırsız · deneme lisansı',
    },
    page: {
      plainHttp: 'Bağlantı şifresiz (http)',
      plainHttpHint: 'Alan adı ve HTTPS gelene kadar şifreler ve oturum ağda şifresiz gider.',
      intro: 'Bu kurulumun Oppy lisansı. Bu sayfayı yalnız süper yöneticiler görür.',
      status: { active: 'Lisans etkin', reminder: 'Lisans bitmek üzere', grace: 'Lisans ek sürede', read_only: 'Oppy salt okunur', none: 'Lisans yok', trial: 'Deneme sürümü', trialEnded: 'Deneme süresi doldu' },
      trialSub: '30 günlük ücretsiz deneme · iki ürün de açık · lisans henüz yüklenmedi',
      trialSubQueued: '30 günlük ücretsiz deneme · iki ürün de açık',
      trialEndedLine: 'Oppy salt okunur. Devam etmek için kurulum kimliğini {{vendorTo}} gönderin ve gelen lisans dosyasını yükleyin; yüklenince her şey hemen açılır.',
      installed: 'Kurulum: {{day}}',
      trialEnd: 'Deneme bitişi: {{day}}',
      trialTermLabel: 'Deneme süresinin %{{percent}} kadarı geçti',
      trialNext: 'Sıradaki lisans {{id}}, {{day}} kendiliğinden başlar; deneme o gün biter.',
      trialNextGap: 'Sıradaki lisans {{id}}, {{day}} kendiliğinden başlar. Deneme {{ends}} biter; arada Oppy salt okunur olur.',
      kindFull: 'Şirket lisansı · kullanıcı sınırı yok',
      kindTrial: 'Deneme lisansı · kullanıcı sınırı yok',
      renew: 'Yenilemek için {{vendor}} ile görüşün; yeni lisans dosyasını buradan yükleyin.',
      graceLine: 'Lisans {{ended}} bitti; her şey çalışıyor. Yeni lisans yüklenmezse Oppy {{day}} salt okunur olur.',
      readOnlyLine: 'Yeni mesaj gönderilemez, kod çalıştırılamaz; eski sohbet ve belgeler açılır. Yeni lisans dosyası yüklenince her şey hemen eski haline döner.',
      noneLine: 'Bu kurulumda geçerli bir lisans yok; Oppy salt okunur. Aşağıdaki kurulum kimliğini {{vendorTo}} gönderip lisans dosyası isteyin.',
      days_one: '{{count}} gün',
      days_other: '{{count}} gün',
      left: 'kaldı · bitiş {{day}}',
      graceLeft: 'ek süre kaldı · salt okunur: {{day}}',
      start: 'Başlangıç: {{day}}',
      end: 'Bitiş: {{day}}',
      termLabel: 'Lisans süresinin %{{percent}} kadarı geçti',
      next: 'Sıradaki lisans {{id}}, {{day}} kendiliğinden başlar.',
      details: 'Lisans bilgileri',
      copy: 'Kopyala',
      copied: 'Kopyalandı',
      boundNote: 'Lisans bu kuruluma bağlıdır; başka bir sunucuya kopyalanınca çalışmaz. Yenileme ya da yeni lisans isterken bu kimliği {{vendorTo}} gönderin.',
      continue: {
        title: 'Denemeden sonra devam etmek için',
        copyTitle: 'Kurulum kimliğini kopyalayın',
        copyText: 'Bu kurulumun kimliği. {{vendorTo}} gönderin.',
        prepareTitle: '{{vendor}} lisansınızı hazırlar',
        prepareText: '{{vendor}} bu kimliğe özel bir lisans dosyası hazırlar.',
        uploadTitle: 'Lisans dosyasını yükleyin',
        uploadText: 'Gelen dosyayı sağ üstteki <b>Lisans dosyası yükle</b> ile yükleyin. Yüklenince deneme biter, lisans başlar.',
      },
      trialEnds: {
        title: 'Deneme bitince ne olur?',
        firstTitle: 'İlk 30 gün',
        first: 'Her şey açık. Üst çubukta yalnız süper yöneticiler kalan günü görür.',
        lastTitle: 'Son 7 gün',
        last: 'Süper yöneticilere 7, 3 ve 1 gün kala bildirim gider; rozet sarıya döner.',
        readOnlyTitle: '30. günün sonunda salt okunur',
        readOnly: 'Ek süre yok. Eski sohbet ve belgeler açılır; yeni mesaj gönderilemez. Lisans yüklenince hemen açılır.',
        foot: 'Deneme her kurulumda bir kez verilir; süre bitince yeniden başlatılamaz.',
      },
      ends: {
        title: 'Süre bitince ne olur?',
        noDelete: 'Hiçbir durumda veri silinmez',
        reminderTitle: 'Bitişten 30 gün önce',
        reminder: 'Süper yöneticilere 30, 7 ve 1 gün kala bildirim gider; üst çubukta kalan gün görünür. Üyeler bir şey görmez.',
        graceTitle: 'Bitişten sonra 14 gün ek süre',
        grace: 'Her şey çalışmaya devam eder. Herkes bir bildirim alır ve üst çubukta kalan günü görür.',
        readOnlyTitle: 'Ek süre bitince salt okunur',
        readOnly: 'Eski sohbet ve belgeler açılır, dışa aktarılır. Yeni mesaj gönderilemez, kod çalıştırılamaz.',
        foot: 'Yeni lisans dosyası yüklenince her şey hemen eski haline döner. İnternet bağlantısı gerekmez.',
      },
      history: {
        title: 'Lisans geçmişi',
        uploaded: 'Yüklendi',
        by: 'Yükleyen',
        started: 'Başladı',
        startedBy: 'Başlatan',
        tool: 'Kurulum aracı',
        state: 'Durum',
        trial: 'deneme',
        freeTrial: '30 günlük deneme',
        states: { trial: 'Deneme', active: 'Etkin', queued: 'Sırada', ended: 'Süresi bitti', invalid: 'Geçersiz' },
        trialEnded: 'Bitti',
        empty: 'Henüz lisans yüklenmedi.',
      },
    },
    upload: {
      title: 'Lisans dosyası yükle',
      drop: '{{vendor}} tarafından gönderilen .oppy-lisans dosyasını seçin ya da buraya bırakın.',
      pick: 'Dosya seç',
      pickAgain: 'Başka dosya seç',
      picked: '{{size}} · az önce seçildi',
      checking: 'Dosya denetleniyor…',
      signatureOk: 'İmza geçerli: dosya {{vendor}} tarafından imzalanmış, değiştirilmemiş',
      installationOk: 'Bu kuruluma ait: kurulum kimliği',
      problems: {
        format: 'Bu bir Oppy lisans dosyası değil.',
        signature: 'Dosya değiştirilmiş ya da {{vendor}} imzası yok.',
        installation: 'Bu dosya başka bir kuruluma ait.',
        expired: 'Bu lisansın süresi geçmiş.',
      },
      installationHint: 'Bu kurulumun kimliği {{id}}. Lisans için bu kimliği {{vendorTo}} gönderin.',
      follows: 'Bu lisans, şimdiki lisans bitince <b>{{day}}</b> kendiliğinden başlar. Arada kesinti olmaz.',
      startsOn: 'Bu lisans <b>{{day}}</b> kendiliğinden başlar.',
      trialNow: 'Yüklenince deneme biter, bu lisans hemen başlar.',
      trialFollows: 'Bu lisans, deneme bitince <b>{{day}}</b> kendiliğinden başlar. Arada kesinti olmaz.',
      trialCut: 'Bu lisans <b>{{day}}</b> kendiliğinden başlar; deneme o gün biter.',
      trialGap: 'Bu lisans <b>{{day}}</b> kendiliğinden başlar. Deneme {{ends}} biter; arada Oppy salt okunur olur.',
      installed: 'Bu lisans zaten yüklü.',
      apply: 'Lisansı yükle',
      applied: 'Lisans {{id}} uygulandı.',
    },
    readOnly: {
      title: 'Oppy şu an salt okunur',
      body: 'Lisans süresi doldu. Eski sohbet ve belgelerinizi açıp dışa aktarabilirsiniz; yeni mesaj gönderilemez. Verileriniz silinmedi, lisans yenilenince kaldığınız yerden devam edersiniz.',
      bodyNone: 'Bu kurulumda geçerli bir lisans yok. Eski sohbet ve belgelerinizi açıp dışa aktarabilirsiniz; yeni mesaj gönderilemez. Verileriniz silinmedi.',
      bodyTrial: 'Ücretsiz deneme süresi doldu. Eski sohbet ve belgelerinizi açıp dışa aktarabilirsiniz; yeni mesaj gönderilemez. Verileriniz silinmedi; lisans yüklenince kaldığınız yerden devam edersiniz.',
      tellAdmin: 'Yöneticinize haber verin.',
      export: 'Sohbeti dışa aktar',
    },
    productOff: {
      title: 'Bu ürün lisansınızda yok',
      body: '{{product}} bu kurulumun lisansında yer almıyor. Kullanmak istiyorsanız yöneticinize haber verin.',
      bodyAdmin: '{{product}} bu kurulumun lisansında yer almıyor. Lisansa eklenmesi için {{vendor}} ile görüşün; yeni lisans dosyasını buradan da yükleyebilirsiniz.',
    },
  },
  microsoft365: { admin: m365AdminTr, user: m365UserTr, chat: m365ChatTr, files: m365FilesTr },
};

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };
export type Messages = DeepStrings<typeof tr>;
